/**
 * Reconciliation View Component - Wizard Style
 * 5-Step Tag-led Part Reconciliation Process
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Tooltip,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  InputAdornment,
  Autocomplete,
  Stepper,
  Step,
  StepLabel,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Drawer,
  Collapse,
  ListItemButton,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CloudUpload as UploadIcon,
  PlayArrow as RunIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Link as LinkIcon,
  CheckCircle as ConfirmedIcon,
  Warning as ReviewIcon,
  Error as NoMatchIcon,
  AutoAwesome as AutoIcon,
  Category as CategoryIcon,
  Factory as ManufacturerIcon,
  ArrowForward as NextIcon,
  ArrowBack as BackIcon,
  LocalOffer as TagIcon,
  Description as BomIcon,
  CompareArrows as ReconcileIcon,
  Download as DownloadIcon,
  Upload as ImportIcon,
  ChevronLeft as CollapseIcon,
  ChevronRight as ExpandIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Visibility as ViewIcon,
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  AddCircleOutline as NewItemIcon,
} from '@mui/icons-material';
import { useBOM } from '../contexts/BOMContext';
import * as bomApi from '../services/bomApi';
import { CustomerFolder } from '../types/bom.types';

// ============================================================================
// Types
// ============================================================================

interface ExtractedTag {
  reference: string;
  component_type: string;
  description: string;
  pages: string;
  verification_status: string;
}

interface BOMItem {
  id: number;
  ref: string;
  qty: number;
  supplier: string;
  supplier_part_no: string;
  customer_part_no: string;
  description: string;
  device_tags: string[];
  raw_device_names: string;
}

const isLockedErpKey = (pp?: string | null) =>
  Boolean(pp && String(pp).trim() && String(pp).trim() !== 'NEW ITEM');

const reconciliationRowTone = (pp?: string, status?: string) => {
  if (pp === 'NEW ITEM') {
    return { bgcolor: '#e3f2fd', borderLeft: '4px solid #2196f3' };
  }
  if (status === 'confirmed') {
    return { bgcolor: '#e8f5e9', borderLeft: '4px solid #4caf50' };
  }
  if (status === 'rejected') {
    return { bgcolor: '#ffebee', borderLeft: '4px solid #f44336' };
  }
  return { bgcolor: '#fff', borderLeft: '4px solid transparent' };
};

const stickyActionSx = (bgcolor: string) => ({
  position: 'sticky' as const,
  right: 0,
  bgcolor,
  zIndex: 2,
  whiteSpace: 'nowrap' as const,
  boxShadow: '-8px 0 8px -8px rgba(0,0,0,0.25)',
});

// ============================================================================
// Step Components
// ============================================================================

// Step 1: Tag Extraction Display
const Step1TagExtraction: React.FC<{
  tags: ExtractedTag[];
  loading: boolean;
  pdfName: string;
}> = ({ tags, loading, pdfName }) => {
  const [filter, setFilter] = useState('');
  
  const filteredTags = useMemo(() => {
    if (!filter) return tags;
    return tags.filter(t => 
      t.reference.toLowerCase().includes(filter.toLowerCase()) ||
      t.component_type?.toLowerCase().includes(filter.toLowerCase())
    );
  }, [tags, filter]);
  
  const tagsByType = useMemo(() => {
    const grouped: Record<string, ExtractedTag[]> = {};
    filteredTags.forEach(tag => {
      const type = tag.component_type || 'UNKNOWN';
      if (!grouped[type]) grouped[type] = [];
      grouped[type].push(tag);
    });
    return grouped;
  }, [filteredTags]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>PDF:</strong> {pdfName} • <strong>Total Tags:</strong> {tags.length}
      </Alert>
      
      <TextField
        size="small"
        placeholder="Filter tags..."
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
        }}
        sx={{ mb: 2, width: 300 }}
      />
      
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {Object.entries(tagsByType).map(([type, typeTags]) => (
          <Card key={type} sx={{ minWidth: 200, flex: '1 1 200px', maxWidth: 300 }}>
            <CardContent>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                {type} ({typeTags.length})
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxHeight: 150, overflow: 'auto' }}>
                {typeTags.map(tag => (
                  <Chip
                    key={tag.reference}
                    label={tag.reference}
                    size="small"
                    variant="outlined"
                    color={tag.verification_status === 'verified' ? 'success' : 'default'}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
      
      <Box sx={{ mt: 2, p: 2, bgcolor: 'success.light', borderRadius: 1, color: 'success.contrastText' }}>
        <Typography variant="body2">
          ✓ <strong>Outcome:</strong> {tags.length} validated functional tags extracted from PDF
        </Typography>
      </Box>
    </Box>
  );
};

// Step 2: BOM Upload and Column Mapping
const Step2BOMUpload: React.FC<{
  folderId: string;
  folderName: string;
  bomItems: BOMItem[];
  setBomItems: (items: BOMItem[]) => void;
  onUploadComplete: () => void;
  showToast: (type: 'info' | 'success' | 'warning' | 'error', msg: string) => void;
}> = ({ folderId, folderName, bomItems, setBomItems, onUploadComplete, showToast }) => {
  const [uploading, setUploading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState<{
    columns: string[];
    rows: Record<string, string>[];
    file: File | null;
  }>({ columns: [], rows: [], file: null });
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [existingUploads, setExistingUploads] = useState<any[]>([]);

  // Load existing BOM uploads for this folder
  useEffect(() => {
    const loadExisting = async () => {
      try {
        const response = await bomApi.listBOMUploads(undefined, folderId);
        if (response.success) {
          setExistingUploads(response.uploads);
          // Auto-load the most recent BOM if available and no items loaded yet
          if (response.uploads.length > 0 && bomItems.length === 0) {
            loadBOMItems(response.uploads[0].id);
          }
        }
      } catch (e) {}
    };
    if (folderId) loadExisting();
  }, [folderId]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const preview = await bomApi.previewBOMColumns(file);
      if (preview.success) {
        setPreviewData({
          columns: preview.columns,
          rows: preview.preview_rows,
          file
        });
        
        // Auto-detect mapping
        const autoMapping: Record<string, string> = {};
        preview.columns.forEach(col => {
          const colLower = col.toLowerCase();
          if (colLower.includes('device') || colLower === 'names' || colLower.includes('tag')) {
            autoMapping.device_tag = col;
          } else if (colLower.includes('scc') || (colLower.includes('customer') && colLower.includes('part'))) {
            autoMapping.customer_part_number = col;
          } else if (colLower.includes('supplier') && colLower.includes('part')) {
            autoMapping.supplier_part_number = col;
          } else if (colLower === 'description' || colLower === 'desc') {
            autoMapping.description = col;
          } else if (colLower === 'qty' || colLower === 'quantity') {
            autoMapping.quantity = col;
          } else if (colLower === 'ref') {
            autoMapping.ref = col;
          } else if (colLower === 'supplier' && !colLower.includes('part')) {
            autoMapping.supplier = col;
          }
        });
        setMapping(autoMapping);
        setPreviewOpen(true);
      } else {
        showToast('error', preview.error || 'Failed to preview file');
      }
    } catch (err: any) {
      showToast('error', err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleUpload = async () => {
    if (!previewData.file || !mapping.device_tag) {
      showToast('error', 'Please map at least the Device Tag column');
      return;
    }
    
    setUploading(true);
    setPreviewOpen(false);
    
    try {
      const response = await bomApi.uploadCustomerBOM(
        previewData.file,
        mapping,
        undefined,
        folderId
      );
      
      if (response.success) {
        showToast('success', `BOM uploaded: ${response.total_items} items parsed`);
        // Auto-load the newly uploaded BOM
        loadBOMItems(response.upload_id);
        // Refresh the uploads list
        const uploads = await bomApi.listBOMUploads(undefined, folderId);
        if (uploads.success) setExistingUploads(uploads.uploads);
        onUploadComplete();
      } else {
        showToast('error', response.error || 'Upload failed');
      }
    } catch (err: any) {
      showToast('error', err.message);
    } finally {
      setUploading(false);
    }
  };

  const loadBOMItems = async (uploadId: string) => {
    try {
      const response = await bomApi.getBOMUploadItems(uploadId);
      if (response.success) {
        const items: BOMItem[] = response.items.map((item: any) => ({
          id: item.id,
          ref: '',
          qty: item.quantity || 1,
          supplier: '',
          supplier_part_no: item.supplier_part_number || '',
          customer_part_no: item.customer_part_number || '',
          description: item.description || '',
          device_tags: item.device_tag ? parseDeviceTags(item.device_tag) : [],
          raw_device_names: item.device_tag || ''
        }));
        setBomItems(items);
        showToast('success', `Loaded ${items.length} BOM items`);
      }
    } catch (err: any) {
      showToast('error', err.message);
    }
  };

  const parseDeviceTags = (tagStr: string): string[] => {
    if (!tagStr) return [];
    const tags: string[] = [];
    const parts = tagStr.split(/[,;]/);
    parts.forEach(part => {
      const trimmed = part.trim();
      if (trimmed) tags.push(trimmed);
    });
    return tags;
  };

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Alert severity="info" sx={{ mb: 2 }}>
        Upload the customer BOM for <strong>{folderName}</strong>. The BOM will be saved to this folder and can be used across multiple extractions.
      </Alert>
      
      {/* Existing Uploads */}
      {existingUploads.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Previously Uploaded BOMs:</Typography>
          <List dense>
            {existingUploads.map(upload => (
              <ListItem 
                key={upload.id}
                secondaryAction={
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Button size="small" onClick={() => loadBOMItems(upload.id)}>
                      Load
                    </Button>
                    <IconButton 
                      size="small" 
                      color="error"
                      onClick={async () => {
                        if (window.confirm(`Delete "${upload.filename}"? This cannot be undone.`)) {
                          const result = await bomApi.deleteBOMUpload(upload.id);
                          if (result.success) {
                            showToast('success', 'BOM deleted');
                            // Refresh the list
                            const uploads = await bomApi.listBOMUploads(undefined, folderId);
                            if (uploads.success) setExistingUploads(uploads.uploads);
                          } else {
                            showToast('error', result.error || 'Failed to delete BOM');
                          }
                        }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                }
              >
                <ListItemIcon><BomIcon /></ListItemIcon>
                <ListItemText 
                  primary={upload.filename}
                  secondary={`${upload.total_items} items • Uploaded ${new Date(upload.created_at).toLocaleDateString()}`}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
      
      {/* Upload New */}
      <Paper sx={{ p: 2, mb: 2, border: '2px dashed', borderColor: 'divider' }}>
        <Box sx={{ textAlign: 'center' }}>
          <Button
            variant="contained"
            component="label"
            startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
            disabled={uploading}
          >
            {uploading ? 'Processing...' : 'Upload New BOM'}
            <input type="file" hidden accept=".xlsx,.xls" onChange={handleFileSelect} />
          </Button>
          <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
            Excel file with Device Names/Tags, Part Numbers, Description
          </Typography>
        </Box>
      </Paper>
      
      {/* Current BOM Items */}
      {bomItems.length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Loaded BOM: {bomItems.length} items
          </Typography>
          <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Device Tags</TableCell>
                  <TableCell>Customer Part #</TableCell>
                  <TableCell>Supplier Part #</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Qty</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bomItems.slice(0, 20).map((item, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      {item.device_tags.length > 0 ? (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {item.device_tags.map((tag, i) => (
                            <Chip key={i} label={tag} size="small" color="primary" variant="outlined" />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">(untagged)</Typography>
                      )}
                    </TableCell>
                    <TableCell>{item.customer_part_no || '-'}</TableCell>
                    <TableCell>{item.supplier_part_no || '-'}</TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {item.description || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>{item.qty}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {bomItems.length > 20 && (
            <Typography variant="caption" color="text.secondary">
              Showing first 20 of {bomItems.length} items
            </Typography>
          )}
          
          <Box sx={{ mt: 2, p: 2, bgcolor: 'success.light', borderRadius: 1, color: 'success.contrastText' }}>
            <Typography variant="body2">
              ✓ <strong>Outcome:</strong> {bomItems.filter(i => i.device_tags.length > 0).length} items with tags, {bomItems.filter(i => i.device_tags.length === 0).length} untagged items
            </Typography>
          </Box>
        </Box>
      )}
      
      {/* Column Mapping Dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Map BOM Columns</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Map the columns from your BOM file. Device Tag is required.
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 3 }}>
            {[
              { key: 'device_tag', label: 'Device Tag / Names *', required: true },
              { key: 'customer_part_number', label: 'Customer Part Number' },
              { key: 'supplier_part_number', label: 'Supplier Part Number' },
              { key: 'description', label: 'Description' },
              { key: 'quantity', label: 'Quantity' },
              { key: 'ref', label: 'Ref / Line #' },
            ].map(field => (
              <FormControl key={field.key} fullWidth size="small">
                <InputLabel>{field.label}</InputLabel>
                <Select
                  value={mapping[field.key] || ''}
                  label={field.label}
                  onChange={(e) => setMapping({ ...mapping, [field.key]: e.target.value })}
                >
                  <MenuItem value="">-- Select --</MenuItem>
                  {previewData.columns.map(col => (
                    <MenuItem key={col} value={col}>{col}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            ))}
          </Box>
          
          <Typography variant="subtitle2" gutterBottom>Preview:</Typography>
          <TableContainer sx={{ maxHeight: 200, overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {previewData.columns.map(col => (
                    <TableCell key={col} sx={{ whiteSpace: 'nowrap' }}>{col}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {previewData.rows.slice(0, 5).map((row, idx) => (
                  <TableRow key={idx}>
                    {previewData.columns.map(col => (
                      <TableCell key={col} sx={{ whiteSpace: 'nowrap' }}>{row[col] || '-'}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleUpload} 
            variant="contained"
            disabled={!mapping.device_tag}
          >
            Upload & Parse
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Step 3: Cross-Reference / Master List Upload
const Step3CrossReference: React.FC<{
  folderId: string;
  folderName: string;
  crossRefs: any[];
  setCrossRefs: (refs: any[]) => void;
  showToast: (type: 'info' | 'success' | 'warning' | 'error', msg: string) => void;
}> = ({ folderId, folderName, crossRefs, setCrossRefs, showToast }) => {
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const response = await bomApi.uploadCrossReference(file, folderId);
      if (response.success) {
        showToast('success', `Cross-reference uploaded: ${response.items_imported} items`);
        const refs = await bomApi.getFolderCrossRefs(folderId);
        if (refs.success) {
          setCrossRefs(refs.items);
        }
      } else {
        showToast('error', response.error || 'Upload failed');
      }
    } catch (err: any) {
      showToast('error', err.message);
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    const loadCrossRefs = async () => {
      try {
        const refs = await bomApi.getFolderCrossRefs(folderId);
        if (refs.success) {
          setCrossRefs(refs.items);
        }
      } catch (e) {}
    };
    if (folderId) loadCrossRefs();
  }, [folderId, setCrossRefs]);

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Alert severity="info" sx={{ mb: 2 }}>
        Upload the <strong>Master Parts List / Cross-Reference</strong> for <strong>{folderName}</strong>. 
        This maps customer part numbers to manufacturer information.
      </Alert>
      
      <Paper sx={{ p: 2, mb: 2, border: '2px dashed', borderColor: 'divider' }}>
        <Box sx={{ textAlign: 'center' }}>
          <Button
            variant="contained"
            component="label"
            startIcon={uploading ? <CircularProgress size={20} /> : <ImportIcon />}
            disabled={uploading}
          >
            {uploading ? 'Processing...' : 'Upload Master Parts List'}
            <input type="file" hidden accept=".xlsx,.xls" onChange={handleUpload} />
          </Button>
          <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
            Expected columns: CUSTOMER REF, MANUFACTURER NAME, MANUFACTURER PART REF, DESCRIPTION
          </Typography>
        </Box>
      </Paper>
      
      {crossRefs.length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Loaded Cross-References: {crossRefs.length} entries
          </Typography>
          <TableContainer component={Paper} sx={{ maxHeight: 250, overflowX: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Customer Ref</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Manufacturer</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Mfr Part #</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {crossRefs.slice(0, 15).map((ref, idx) => (
                  <TableRow key={idx}>
                    <TableCell><code>{ref.customer_ref}</code></TableCell>
                    <TableCell>{ref.manufacturer_name || '-'}</TableCell>
                    <TableCell>{ref.manufacturer_part_ref || '-'}</TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {ref.description || '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Box sx={{ mt: 2, p: 2, bgcolor: 'success.light', borderRadius: 1, color: 'success.contrastText' }}>
            <Typography variant="body2">
              ✓ <strong>Outcome:</strong> {crossRefs.length} cross-reference mappings loaded for triangulation
            </Typography>
          </Box>
        </Box>
      )}
      
      {crossRefs.length === 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          No cross-reference uploaded yet. This step is optional but improves matching accuracy.
        </Alert>
      )}
    </Box>
  );
};

// Step 4: Reconciliation Results (Editable)
const StepReconciliationResults: React.FC<{
  results: any;
  loading: boolean;
  onRunReconciliation: () => void;
  onExportExcel: () => void;
  onExportErp?: () => void;
  onRestart?: () => void;  // Optional - only shown in history view
  showToast: (type: 'info' | 'success' | 'warning' | 'error', msg: string) => void;
}> = ({ results, loading, onRunReconciliation, onExportExcel, onExportErp, onRestart, showToast }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValues, setEditValues] = useState<Record<string, any>>({});
  const [localMatches, setLocalMatches] = useState<any[]>([]);
  const [localUntagged, setLocalUntagged] = useState<any[]>([]);
  const [searchErpQuery, setSearchErpQuery] = useState('');
  const [searchErpResults, setSearchErpResults] = useState<any[]>([]);
  const [searchingErp, setSearchingErp] = useState(false);
  const [diagnosticMatch, setDiagnosticMatch] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState(''); // Search filter for results table
  const [editingUntaggedId, setEditingUntaggedId] = useState<number | null>(null);
  const [editUntaggedValues, setEditUntaggedValues] = useState<Record<string, any>>({});
  const [searchingForItemId, setSearchingForItemId] = useState<number | null>(null); // Track which item we're searching ERP for
  const [componentTypes, setComponentTypes] = useState<{ name: string; display_name: string }[]>([]);
  
  // Sort state
  const [sortField, setSortField] = useState<string>('extracted_tag');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [tableLayout, setTableLayout] = useState<'fit' | 'wide'>('fit');
  
  // Batch apply dialog state
  const [batchApplyDialog, setBatchApplyDialog] = useState<{
    open: boolean;
    matchId: number | null;
    matchingLines: any[];
    matchBy: 'customer' | 'supplier' | 'both';
    editValues: Record<string, any>;
  }>({ open: false, matchId: null, matchingLines: [], matchBy: 'both', editValues: {} });
  
  // Find ERP Matches dialog state
  const [findMatchesDialog, setFindMatchesDialog] = useState<{
    open: boolean;
    match: any;
    erpCandidates: any[];
    loading: boolean;
  }>({ open: false, match: null, erpCandidates: [], loading: false });

  // Fetch component types for dropdowns
  React.useEffect(() => {
    const loadComponentTypes = async () => {
      try {
        const response = await fetch('/api/component-types/list');
        const data = await response.json();
        if (data.success && data.types) {
          setComponentTypes(data.types.map((t: any) => ({ 
            name: t.name, 
            display_name: t.display_name || t.name 
          })));
        }
      } catch (error) {
        console.error('Failed to load component types:', error);
      }
    };
    loadComponentTypes();
  }, []);

  // Sync local state with results
  React.useEffect(() => {
    if (results?.matches) {
      setLocalMatches(results.matches);
    }
    if (results?.untagged_items) {
      setLocalUntagged(results.untagged_items);
    }
  }, [results]);

  // Memoized filtered and sorted matches based on search and tab
  const filteredMatches = React.useMemo(() => {
    let filtered = localMatches.filter((m: any) => {
      // KEEP rejected items - they should show in their original tab with red highlight
      // (previously: if (m.status === 'rejected') return false;)
      
      // Tab filter - MUTUALLY EXCLUSIVE conditions to avoid items appearing in multiple tabs
      // Key rule: No ERP PP = No Match (regardless of confidence)
      const hasErpMatch = !!(m.erp_pp_number && String(m.erp_pp_number).trim());
      
      // Check if it's a NEW ITEM (flagged for ERP addition)
      const isNewItem = m.erp_pp_number === 'NEW ITEM';
      
      // Tab 0: High Confidence - has real ERP match AND confidence >= 0.5
      if (activeTab === 0 && !(hasErpMatch && !isNewItem && m.confidence >= 0.5)) return false;
      // Tab 1: Needs Review - has real ERP match AND confidence < 0.5
      if (activeTab === 1 && !(hasErpMatch && !isNewItem && m.confidence < 0.5)) return false;
      // Tab 2: No Match - NO real ERP match OR is NEW ITEM (needs to be added to ERP)
      if (activeTab === 2 && hasErpMatch && !isNewItem) return false;
      
      // Search filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        const searchFields = [
          m.extracted_tag,
          m.customer_part_number,
          m.customer_supplier_part,
          m.erp_pp_number,
          m.erp_description,
          m.extracted_description,
          m.erp_manufacturer_name,
          m.erp_vendor_item_no,
          m.component_type,
        ].filter(Boolean).map(f => String(f).toLowerCase());
        return searchFields.some(f => f.includes(query));
      }
      return true;
    });
    
    // Apply sorting
    filtered.sort((a: any, b: any) => {
      let aVal: any = a[sortField];
      let bVal: any = b[sortField];
      
      // For description, use fallbacks: erp_description -> bom_description -> extracted_description
      if (sortField === 'erp_description') {
        aVal = a.erp_description || a.bom_description || a.extracted_description;
        bVal = b.erp_description || b.bom_description || b.extracted_description;
      }
      
      // Handle null/undefined - push them to the end
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return sortDirection === 'asc' ? 1 : -1;
      if (bVal == null) return sortDirection === 'asc' ? -1 : 1;
      
      // Handle numbers
      if (sortField === 'confidence' || sortField === 'quantity') {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      } else {
        aVal = String(aVal).toLowerCase().trim();
        bVal = String(bVal).toLowerCase().trim();
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    
    return filtered;
  }, [localMatches, activeTab, searchQuery, sortField, sortDirection]);

  // Memoized filtered untagged items
  const filteredUntagged = React.useMemo(() => {
    let filtered = [...localUntagged]; // Always create a new array
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((item: any) => {
        const searchFields = [
          item.customer_part_number,
          item.supplier_part_number,
          item.description,
          item.device_tag,
        ].filter(Boolean).map(f => String(f).toLowerCase());
        return searchFields.some(f => f.includes(query));
      });
    }
    
    // Apply sorting - map field names to untagged item structure
    filtered.sort((a: any, b: any) => {
      // Map sort field names: untagged items use different field names
      let mappedField = sortField;
      if (sortField === 'customer_supplier_part') mappedField = 'supplier_part_number';
      if (sortField === 'erp_description') mappedField = 'description'; // Fall back to BOM description
      if (sortField === 'extracted_tag') mappedField = 'customer_part_number'; // Untagged has no tag, sort by customer part
      
      let aVal: any = a[mappedField];
      let bVal: any = b[mappedField];
      
      // For description, also check erp_description if available
      if (sortField === 'erp_description') {
        aVal = a.erp_description || a.description;
        bVal = b.erp_description || b.description;
      }
      
      // Handle null/undefined - push them to the end
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return sortDirection === 'asc' ? 1 : -1;
      if (bVal == null) return sortDirection === 'asc' ? -1 : 1;
      
      if (sortField === 'quantity') {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      } else {
        aVal = String(aVal).toLowerCase().trim();
        bVal = String(bVal).toLowerCase().trim();
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    
    return filtered;
  }, [localUntagged, searchQuery, sortField, sortDirection]);

  const handleEdit = (match: any) => {
    setEditingId(match.id);
    // Store ALL editable fields so they can be reverted on cancel
    setEditValues({
      erp_pp_number: match.erp_pp_number || '',
      erp_description: match.erp_description || '',
      erp_vendor_item_no: match.erp_vendor_item_no || '',
      erp_manufacturer_code: match.erp_manufacturer_code || '',
      erp_manufacturer_name: match.erp_manufacturer_name || '',
      erp_item_category: match.erp_item_category || '',
      component_type: match.component_type || '',
      quantity: match.quantity || 1,
    });
  };

  const handleSaveEdit = async (matchId: number, skipBatchCheck: boolean = false) => {
    try {
      const currentMatch = localMatches.find(m => m.id === matchId);
      if (!currentMatch) return;
      
      const fields = { ...editValues };
      if (isLockedErpKey(currentMatch.erp_pp_number)) {
        delete fields.erp_pp_number;
        delete fields.erp_vendor_item_no;
      }
      const response = await fetch(`/api/reconciliation/match/${matchId}/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fields })
      });
      const data = await response.json();
      
      if (data.success) {
        // Update local state with ALL edit values (including ERP lookup fields)
        setLocalMatches(prev => prev.map(m => 
          m.id === matchId ? { 
            ...m, 
            ...data.match,
            // Ensure all editValues are applied (in case backend doesn't return them)
            erp_pp_number: isLockedErpKey(currentMatch.erp_pp_number)
              ? currentMatch.erp_pp_number
              : (editValues.erp_pp_number || m.erp_pp_number),
            erp_description: editValues.erp_description || m.erp_description,
            erp_vendor_item_no: isLockedErpKey(currentMatch.erp_pp_number)
              ? currentMatch.erp_vendor_item_no
              : (editValues.erp_vendor_item_no || m.erp_vendor_item_no),
            erp_manufacturer_code: editValues.erp_manufacturer_code || m.erp_manufacturer_code,
            erp_manufacturer_name: editValues.erp_manufacturer_name || m.erp_manufacturer_name,
            erp_item_category: editValues.erp_item_category || m.erp_item_category,
            component_type: editValues.component_type || m.component_type,
            quantity: editValues.quantity || m.quantity,
          } : m
        ));
        showToast('success', 'Changes saved');
        setEditingId(null);
        
        // Check for matching lines to apply edits (only if we have ERP data and not skipping)
        if (!skipBatchCheck && editValues.erp_pp_number) {
          const customerPart = currentMatch.customer_part_number;
          const supplierPart = currentMatch.customer_supplier_part || currentMatch.supplier_part_number;
          
          // Find ALL other lines with matching customer or supplier part (excluding current line)
          // Include lines with existing ERP data too - user may want to update all matching
          const matchingInMatches = localMatches.filter(m => 
            m.id !== matchId && 
            m.status !== 'confirmed' && m.status !== 'rejected' &&
            ((customerPart && m.customer_part_number === customerPart) ||
             (supplierPart && (m.customer_supplier_part === supplierPart || m.supplier_part_number === supplierPart)))
          );
          
          // Also check untagged items
          const matchingInUntagged = localUntagged.filter(item => 
            item.status !== 'confirmed' && item.status !== 'rejected' &&
            ((customerPart && item.customer_part_number === customerPart) ||
             (supplierPart && item.supplier_part_number === supplierPart))
          ).map(u => ({ ...u, isUntagged: true }));
          
          const matchingLines = [...matchingInMatches, ...matchingInUntagged];
          
          if (matchingLines.length > 0) {
            // Show batch apply dialog
            setBatchApplyDialog({
              open: true,
              matchId: matchId,
              matchingLines: matchingLines,
              matchBy: 'both',
              editValues: { ...editValues }
            });
          }
        }
        
        setEditValues({}); // Clear edit values
      } else {
        showToast('error', data.error || 'Failed to save');
      }
    } catch (e) {
      showToast('error', 'Failed to save changes');
    }
  };
  
  // Apply to all matching lines (confirm, reject, or update depending on action)
  const handleBatchApply = async () => {
    const { matchingLines, editValues: batchValues, matchId } = batchApplyDialog;
    if (batchValues._action === 'new_item') {
      const sourceUntagged = Boolean(batchValues._sourceUntagged);
      let successCount = 0;
      const rows = [
        ...(matchId != null ? [{ id: matchId, isUntagged: sourceUntagged, row: sourceUntagged
          ? localUntagged.find(item => item.id === matchId)
          : localMatches.find(m => m.id === matchId) }] : []),
        ...matchingLines.map(line => ({ id: line.id, isUntagged: Boolean(line.isUntagged), row: line })),
      ];
      for (const entry of rows) {
        try {
          await postNewItem(entry.id, entry.isUntagged, entry.row);
          successCount++;
        } catch (e) {
          console.error('Failed to flag line as NEW ITEM', entry.id, e);
        }
      }
      showToast('success', `Flagged ${successCount} lines as NEW ITEM`);
      setBatchApplyDialog(prev => ({ ...prev, open: false }));
      return;
    }
    const isConfirmAction = batchValues._action === 'confirm';
    const isRejectAction = batchValues._action === 'reject';
    const isSourceUntagged = batchValues._sourceUntagged;
    
    if (!matchingLines || matchingLines.length === 0) {
      setBatchApplyDialog(prev => ({ ...prev, open: false }));
      return;
    }
    
    let successCount = 0;
    
    // First, handle the current item (source item)
    if (matchId) {
      try {
        if (isConfirmAction) {
          if (isSourceUntagged) {
            setLocalUntagged(prev => prev.map(item => 
              item.id === matchId ? { ...item, status: 'confirmed' } : item
            ));
            successCount++;
          } else {
            const response = await bomApi.confirmReconciliationMatch(matchId);
            if (response.success) {
              setLocalMatches(prev => prev.map(m => 
                m.id === matchId ? { ...m, status: 'confirmed', user_confirmed: true } : m
              ));
              successCount++;
            }
          }
        } else if (isRejectAction) {
          if (isSourceUntagged) {
            setLocalUntagged(prev => prev.map(item => 
              item.id === matchId ? { ...item, status: 'rejected' } : item
            ));
            successCount++;
          } else {
            const response = await bomApi.rejectReconciliationMatch(matchId);
            if (response.success) {
              setLocalMatches(prev => prev.map(m => 
                m.id === matchId ? { ...m, status: 'rejected', auto_accepted: false, user_confirmed: false } : m
              ));
              successCount++;
            }
          }
        }
      } catch (e) {
        console.error('Failed to process current item:', matchId);
      }
    }
    
    // Then handle all matching lines
    for (const line of matchingLines) {
      const isLineUntagged = line.isUntagged;
      
      try {
        if (isConfirmAction) {
          // Confirm action
          if (isLineUntagged) {
            setLocalUntagged(prev => prev.map(item => 
              item.id === line.id ? { ...item, status: 'confirmed' } : item
            ));
            successCount++;
          } else {
            const response = await bomApi.confirmReconciliationMatch(line.id);
            if (response.success) {
              setLocalMatches(prev => prev.map(m => 
                m.id === line.id ? { ...m, status: 'confirmed', user_confirmed: true } : m
              ));
              successCount++;
            }
          }
        } else if (isRejectAction) {
          // Reject action
          if (isLineUntagged) {
            setLocalUntagged(prev => prev.map(item => 
              item.id === line.id ? { ...item, status: 'rejected' } : item
            ));
            successCount++;
          } else {
            const response = await bomApi.rejectReconciliationMatch(line.id);
            if (response.success) {
              setLocalMatches(prev => prev.map(m => 
                m.id === line.id ? { ...m, status: 'rejected', auto_accepted: false, user_confirmed: false } : m
              ));
              successCount++;
            }
          }
        } else {
          // Update action - apply ERP data
          if (isLineUntagged) {
            // Update untagged item locally
            setLocalUntagged(prev => prev.map(item => 
              item.id === line.id ? { 
                ...item, 
                erp_pp_number: batchValues.erp_pp_number || item.erp_pp_number,
                erp_description: batchValues.erp_description || item.erp_description,
                erp_vendor_item_no: batchValues.erp_vendor_item_no || item.erp_vendor_item_no,
                erp_manufacturer_code: batchValues.erp_manufacturer_code || item.erp_manufacturer_code,
                erp_manufacturer_name: batchValues.erp_manufacturer_name || item.erp_manufacturer_name,
                erp_item_category: batchValues.erp_item_category || item.erp_item_category,
              } : item
            ));
            successCount++;
          } else {
            const response = await fetch(`/api/reconciliation/match/${line.id}/update`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ fields: batchValues })
            });
            const data = await response.json();
            if (data.success) {
              successCount++;
              setLocalMatches(prev => prev.map(m => 
                m.id === line.id ? { 
                  ...m, 
                  ...data.match,
                  erp_pp_number: batchValues.erp_pp_number || m.erp_pp_number,
                  erp_description: batchValues.erp_description || m.erp_description,
                  erp_vendor_item_no: batchValues.erp_vendor_item_no || m.erp_vendor_item_no,
                  erp_manufacturer_code: batchValues.erp_manufacturer_code || m.erp_manufacturer_code,
                  erp_manufacturer_name: batchValues.erp_manufacturer_name || m.erp_manufacturer_name,
                  erp_item_category: batchValues.erp_item_category || m.erp_item_category,
                  confidence: 0.8,
                } : m
              ));
            }
          }
        }
      } catch (e) {
        console.error('Failed to process line:', line.id, e);
      }
    }
    
    const action = isConfirmAction ? 'Confirmed' : isRejectAction ? 'Rejected' : 'Applied ERP data to';
    showToast('success', `${action} ${successCount} lines`);
    setBatchApplyDialog(prev => ({ ...prev, open: false }));
  };
  
  // Handle "Just This One" - process only the current match
  const handleJustThisOne = async () => {
    const { matchId, editValues } = batchApplyDialog;
    if (editValues._action === 'new_item' && matchId != null) {
      const sourceUntagged = Boolean(editValues._sourceUntagged);
      const row = sourceUntagged
        ? localUntagged.find(item => item.id === matchId)
        : localMatches.find(m => m.id === matchId);
      try {
        await postNewItem(matchId, sourceUntagged, row);
        showToast('success', 'Flagged this line as NEW ITEM');
      } catch (e: any) {
        showToast('error', e?.message || 'Failed to flag as new item');
      }
      setBatchApplyDialog(prev => ({ ...prev, open: false }));
      return;
    }
    const isConfirmAction = editValues._action === 'confirm';
    const isRejectAction = editValues._action === 'reject';
    const isSourceUntagged = editValues._sourceUntagged;
    
    if (matchId) {
      try {
        if (isConfirmAction) {
          if (isSourceUntagged) {
            setLocalUntagged(prev => prev.map(item => 
              item.id === matchId ? { ...item, status: 'confirmed' } : item
            ));
            showToast('success', 'Item confirmed');
          } else {
            const response = await bomApi.confirmReconciliationMatch(matchId);
            if (response.success) {
              setLocalMatches(prev => prev.map(m => 
                m.id === matchId ? { ...m, status: 'confirmed', user_confirmed: true } : m
              ));
              showToast('success', 'Match confirmed');
            }
          }
        } else if (isRejectAction) {
          if (isSourceUntagged) {
            setLocalUntagged(prev => prev.map(item => 
              item.id === matchId ? { ...item, status: 'rejected' } : item
            ));
            showToast('success', 'Item rejected');
          } else {
            const response = await bomApi.rejectReconciliationMatch(matchId);
            if (response.success) {
              setLocalMatches(prev => prev.map(m => 
                m.id === matchId ? { ...m, status: 'rejected', auto_accepted: false, user_confirmed: false } : m
              ));
              showToast('success', 'Match rejected');
            }
          }
        }
      } catch (e) {
        showToast('error', `Failed to ${isConfirmAction ? 'confirm' : 'reject'}`);
      }
    }
    
    setBatchApplyDialog(prev => ({ ...prev, open: false }));
  };

  // Find potential ERP matches for a reconciliation item
  const handleFindErpMatches = async (match: any) => {
    setFindMatchesDialog({ open: true, match, erpCandidates: [], loading: true });
    
    try {
      // Search by customer part number, supplier part number, and existing ERP PP# if any
      const searchTerms = [
        match.customer_part_number,
        match.customer_supplier_part,
        match.erp_pp_number, // Include existing ERP PP# if already matched
        match.extracted_tag,
        // Also try without hyphens for flexible matching
        match.customer_part_number?.replace(/-/g, ''),
        match.customer_supplier_part?.replace(/-/g, ''),
      ].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i); // Dedupe
      
      const allResults: any[] = [];
      const seenPPs = new Set<string>();
      
      for (const term of searchTerms) {
        const response = await fetch(`/api/reconciliation/search-erp?q=${encodeURIComponent(term)}&limit=10`);
        const data = await response.json();
        if (data.success && data.results) {
          for (const result of data.results) {
            // Dedupe by PP number
            if (!seenPPs.has(result.pp_number || result.internal_part_number)) {
              seenPPs.add(result.pp_number || result.internal_part_number);
              allResults.push(result);
            }
          }
        }
      }
      
      setFindMatchesDialog(prev => ({ ...prev, erpCandidates: allResults, loading: false }));
      
      if (allResults.length === 0) {
        showToast('info', 'No potential ERP matches found');
      }
    } catch (e) {
      showToast('error', 'Failed to search ERP');
      setFindMatchesDialog(prev => ({ ...prev, loading: false }));
    }
  };
  
  // Find ERP matches for untagged items - uses same logic but for untagged BOM items
  const handleFindErpMatchesForUntagged = async (item: any) => {
    // Convert untagged item to match-like structure for the dialog
    const matchLike = {
      ...item,
      id: item.id,
      extracted_tag: '(untagged)',
      isUntagged: true, // Flag to handle apply differently
    };
    
    setFindMatchesDialog({ open: true, match: matchLike, erpCandidates: [], loading: true });
    
    try {
      const searchTerms = [
        item.customer_part_number,
        item.supplier_part_number,
        item.erp_pp_number,
        item.customer_part_number?.replace(/-/g, ''),
        item.supplier_part_number?.replace(/-/g, ''),
      ].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i);
      
      const allResults: any[] = [];
      const seenPPs = new Set<string>();
      
      for (const term of searchTerms) {
        const response = await fetch(`/api/reconciliation/search-erp?q=${encodeURIComponent(term)}&limit=10`);
        const data = await response.json();
        if (data.success && data.results) {
          for (const result of data.results) {
            if (!seenPPs.has(result.pp_number || result.internal_part_number)) {
              seenPPs.add(result.pp_number || result.internal_part_number);
              allResults.push(result);
            }
          }
        }
      }
      
      setFindMatchesDialog(prev => ({ ...prev, erpCandidates: allResults, loading: false }));
      
      if (allResults.length === 0) {
        showToast('info', 'No potential ERP matches found');
      }
    } catch (e) {
      showToast('error', 'Failed to search ERP');
      setFindMatchesDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // Apply selected ERP match to the reconciliation item (or untagged item)
  const handleApplyErpMatch = async (erpPart: any) => {
    const match = findMatchesDialog.match;
    if (!match) return;
    
    const isUntagged = match.isUntagged;
    
    try {
      const fields = {
        erp_pp_number: erpPart.pp_number || erpPart.internal_part_number,
        erp_description: erpPart.description,
        erp_vendor_item_no: erpPart.vendor_item_no || erpPart.external_reference || '',
        erp_manufacturer_code: erpPart.manufacturer_code || '',
        erp_manufacturer_name: erpPart.manufacturer_name || '',
        erp_item_category: erpPart.item_category || '',
      };
      
      // Different endpoint for untagged items
      const endpoint = isUntagged 
        ? `/api/reconciliation/untagged/${match.id}/update`
        : `/api/reconciliation/match/${match.id}/update`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fields })
      });
      const data = await response.json();
      
      if (data.success) {
        // Update local state based on type
        if (isUntagged) {
          setLocalUntagged(prev => prev.map(m => 
            m.id === match.id ? { 
              ...m, 
              ...data.match || data.item,
              ...fields,
            } : m
          ));
        } else {
          setLocalMatches(prev => prev.map(m => 
            m.id === match.id ? { 
              ...m, 
              ...data.match,
              ...fields,
              confidence: 0.8, // Boost confidence since user selected
            } : m
          ));
        }
        showToast('success', `Applied ERP match: ${fields.erp_pp_number}`);
        
        // Check for other lines with same part numbers that aren't matched yet
        const customerPart = match.customer_part_number;
        const supplierPart = match.customer_supplier_part || match.supplier_part_number;
        
        // Check both matches and untagged items for batch apply
        const matchingLines = localMatches.filter(m => 
          m.id !== match.id && // Exclude current line
          !m.erp_pp_number && // Only lines without ERP match
          m.status !== 'confirmed' && m.status !== 'rejected' && // Not already processed
          ((customerPart && m.customer_part_number === customerPart) ||
           (supplierPart && m.customer_supplier_part === supplierPart))
        );
        
        // Close find dialog first
        setFindMatchesDialog(prev => ({ ...prev, open: false }));
        
        // If there are matching lines, show batch apply dialog
        if (matchingLines.length > 0) {
          setBatchApplyDialog({
            open: true,
            matchId: match.id,
            matchingLines,
            matchBy: 'both',
            editValues: fields,
          });
        }
      } else {
        showToast('error', data.error || 'Failed to apply match');
      }
    } catch (e) {
      showToast('error', 'Failed to apply match');
    }
  };

  // Apply selected ERP match to ALL items with matching customer/supplier part numbers
  const handleApplyErpMatchToAll = async (erpPart: any) => {
    const match = findMatchesDialog.match;
    if (!match) return;
    
    const fields = {
      erp_pp_number: erpPart.pp_number || erpPart.internal_part_number,
      erp_description: erpPart.description,
      erp_vendor_item_no: erpPart.vendor_item_no || erpPart.external_reference || '',
      erp_manufacturer_code: erpPart.manufacturer_code || '',
      erp_manufacturer_name: erpPart.manufacturer_name || '',
      erp_item_category: erpPart.item_category || '',
    };
    
    // Find ALL matching lines based on customer/supplier part numbers (including those with existing ERP data)
    const customerPart = match.customer_part_number;
    const supplierPart = match.customer_supplier_part || match.supplier_part_number;
    
    // Get ALL matching items in both matches and untagged (will update/overwrite)
    const matchingInMatches = localMatches.filter(m => 
      m.status !== 'rejected' &&
      ((customerPart && m.customer_part_number === customerPart) ||
       (supplierPart && (m.customer_supplier_part === supplierPart || m.supplier_part_number === supplierPart)))
    );
    
    const matchingInUntagged = localUntagged.filter(item => 
      item.status !== 'rejected' &&
      ((customerPart && item.customer_part_number === customerPart) ||
       (supplierPart && item.supplier_part_number === supplierPart))
    );
    
    let successCount = 0;
    
    // Apply to all matching tagged items
    for (const line of matchingInMatches) {
      try {
        const response = await fetch(`/api/reconciliation/match/${line.id}/update`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ fields })
        });
        const data = await response.json();
        if (data.success) {
          successCount++;
          setLocalMatches(prev => prev.map(m => 
            m.id === line.id ? { 
              ...m, 
              ...data.match,
              ...fields,
              confidence: 0.8,
            } : m
          ));
        }
      } catch (e) {
        console.error('Failed to update match:', line.id, e);
      }
    }
    
    // Apply to all matching untagged items (local update only)
    for (const item of matchingInUntagged) {
      setLocalUntagged(prev => prev.map(u => 
        u.id === item.id ? { 
          ...u, 
          ...fields,
        } : u
      ));
      successCount++;
    }
    
    // Close dialog
    setFindMatchesDialog(prev => ({ ...prev, open: false }));
    
    showToast('success', `Applied ERP match to ${successCount} items`);
  };

  const customerPartSiblings = (source: any, sourceId: number, sourceUntagged: boolean) => {
    const customerPart = String(source?.customer_part_number || '').trim();
    if (!customerPart) return [];
    const matchingInMatches = localMatches.filter(m =>
      (sourceUntagged || m.id !== sourceId) &&
      m.erp_pp_number !== 'NEW ITEM' &&
      String(m.customer_part_number || '').trim() === customerPart
    );
    const matchingInUntagged = localUntagged.filter(item =>
      (!sourceUntagged || item.id !== sourceId) &&
      item.erp_pp_number !== 'NEW ITEM' &&
      String(item.customer_part_number || '').trim() === customerPart
    ).map(u => ({ ...u, isUntagged: true }));
    return [...matchingInMatches, ...matchingInUntagged];
  };

  const postNewItem = async (id: number, isUntagged: boolean, row: any) => {
    const fields = {
      erp_pp_number: 'NEW ITEM',
      erp_description: row?.bom_description || row?.description || row?.extracted_description || '',
      erp_vendor_item_no: '',
      erp_manufacturer_code: '',
      erp_manufacturer_name: '',
      erp_item_category: '',
    };
    const url = isUntagged
      ? `/api/reconciliation/untagged/${id}/update`
      : `/api/reconciliation/match/${id}/update`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fields }),
    });
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to flag as new item');
    }
    const saved = data.match || data.item || {};
    const patch = {
      erp_pp_number: 'NEW ITEM',
      erp_description: saved.erp_description || fields.erp_description,
      erp_vendor_item_no: '',
      erp_manufacturer_code: '',
      erp_manufacturer_name: '',
      erp_item_category: '',
      status: 'new_item',
      confidence: 0,
    };
    if (isUntagged) {
      setLocalUntagged(prev => prev.map(item => item.id === id ? { ...item, ...patch } : item));
    } else {
      setLocalMatches(prev => prev.map(m => m.id === id ? { ...m, ...patch } : m));
    }
  };

  // Flag item as NEW ITEM. Same customer part number can be applied to every line.
  const handleFlagAsNewItem = async (matchId: number, skipBatchCheck: boolean = false) => {
    const currentMatch = localMatches.find(m => m.id === matchId);
    if (!currentMatch) return;
    if (!skipBatchCheck) {
      const matchingLines = customerPartSiblings(currentMatch, matchId, false);
      if (matchingLines.length > 0) {
        setBatchApplyDialog({
          open: true,
          matchId,
          matchingLines,
          matchBy: 'customer',
          editValues: {
            erp_pp_number: 'NEW ITEM',
            erp_description: currentMatch.bom_description || currentMatch.extracted_description || '',
            _action: 'new_item',
          },
        });
        return;
      }
    }
    try {
      await postNewItem(matchId, false, currentMatch);
      showToast('success', 'Flagged as NEW ITEM. Description is the customer BOM description.');
    } catch (e: any) {
      showToast('error', e?.message || 'Failed to flag as new item');
    }
  };

  const handleFlagUntaggedAsNewItem = async (itemId: number, skipBatchCheck: boolean = false) => {
    const currentItem = localUntagged.find(item => item.id === itemId);
    if (!currentItem) return;
    if (!skipBatchCheck) {
      const matchingLines = customerPartSiblings(currentItem, itemId, true);
      if (matchingLines.length > 0) {
        setBatchApplyDialog({
          open: true,
          matchId: itemId,
          matchingLines,
          matchBy: 'customer',
          editValues: {
            erp_pp_number: 'NEW ITEM',
            erp_description: currentItem.description || '',
            _action: 'new_item',
            _sourceUntagged: true,
          },
        });
        return;
      }
    }
    try {
      await postNewItem(itemId, true, currentItem);
      showToast('success', 'Flagged as NEW ITEM. Description is the customer BOM description.');
    } catch (e: any) {
      showToast('error', e?.message || 'Failed to flag as new item');
    }
  };

  const handleConfirm = async (matchId: number, skipBatchCheck: boolean = false) => {
    const currentMatch = localMatches.find(m => m.id === matchId);
    if (!currentMatch) return;
    
    // Check for other lines with same part numbers that aren't confirmed yet
    if (!skipBatchCheck) {
      const customerPart = currentMatch.customer_part_number;
      const supplierPart = currentMatch.customer_supplier_part || currentMatch.supplier_part_number;
      
      // Find matching lines in both localMatches and localUntagged
      const matchingInMatches = localMatches.filter(m => 
        m.id !== matchId && // Exclude current line
        m.status !== 'confirmed' && m.status !== 'rejected' && // Not already processed
        ((customerPart && m.customer_part_number === customerPart) ||
         (supplierPart && (m.customer_supplier_part === supplierPart || m.supplier_part_number === supplierPart)))
      );
      
      const matchingInUntagged = localUntagged.filter(item => 
        item.status !== 'confirmed' && item.status !== 'rejected' &&
        ((customerPart && item.customer_part_number === customerPart) ||
         (supplierPart && item.supplier_part_number === supplierPart))
      ).map(u => ({ ...u, isUntagged: true }));
      
      const matchingLines = [...matchingInMatches, ...matchingInUntagged];
      
      // If there are matching lines, show the batch apply dialog
      if (matchingLines.length > 0) {
        setBatchApplyDialog({
          open: true,
          matchId: matchId,
          matchingLines,
          matchBy: 'both',
          editValues: {
            erp_pp_number: currentMatch.erp_pp_number,
            erp_description: currentMatch.erp_description,
            erp_vendor_item_no: currentMatch.erp_vendor_item_no,
            erp_manufacturer_code: currentMatch.erp_manufacturer_code,
            erp_manufacturer_name: currentMatch.erp_manufacturer_name,
            erp_item_category: currentMatch.erp_item_category,
            _action: 'confirm', // Flag to indicate this is a confirm action
          },
        });
        return; // Don't confirm yet, wait for user choice
      }
    }
    
    // No matching lines or skipBatchCheck=true, just confirm this one
    try {
      const response = await bomApi.confirmReconciliationMatch(matchId);
      if (response.success) {
        // Update local state
        setLocalMatches(prev => prev.map(m => 
          m.id === matchId ? { ...m, status: 'confirmed', user_confirmed: true } : m
        ));
        showToast('success', 'Match confirmed');
      }
    } catch (e) {
      showToast('error', 'Failed to confirm');
    }
  };

  const handleReject = async (matchId: number, skipBatchCheck: boolean = false) => {
    const currentMatch = localMatches.find(m => m.id === matchId);
    if (!currentMatch) return;
    
    // Check for other lines with same part numbers that aren't rejected yet
    if (!skipBatchCheck) {
      const customerPart = currentMatch.customer_part_number;
      const supplierPart = currentMatch.customer_supplier_part || currentMatch.supplier_part_number;
      
      // Find matching lines in both localMatches and localUntagged
      const matchingInMatches = localMatches.filter(m => 
        m.id !== matchId && // Exclude current line
        m.status !== 'rejected' && // Not already rejected
        ((customerPart && m.customer_part_number === customerPart) ||
         (supplierPart && (m.customer_supplier_part === supplierPart || m.supplier_part_number === supplierPart)))
      );
      
      const matchingInUntagged = localUntagged.filter(item => 
        item.status !== 'rejected' &&
        ((customerPart && item.customer_part_number === customerPart) ||
         (supplierPart && item.supplier_part_number === supplierPart))
      ).map(u => ({ ...u, isUntagged: true }));
      
      const matchingLines = [...matchingInMatches, ...matchingInUntagged];
      
      // If there are OTHER matching lines (more than 0), show the batch dialog
      if (matchingLines.length > 0) {
        setBatchApplyDialog({
          open: true,
          matchId: matchId,
          matchingLines,
          matchBy: 'both',
          editValues: {
            _action: 'reject', // Flag to indicate this is a reject action
          },
        });
        return; // Don't reject yet, wait for user choice
      }
    }
    
    // No other matching lines or skipBatchCheck=true, just reject this one
    try {
      const response = await bomApi.rejectReconciliationMatch(matchId);
      if (response.success) {
        // Update local state - reset ALL confirmation flags
        setLocalMatches(prev => prev.map(m => 
          m.id === matchId ? { ...m, status: 'rejected', auto_accepted: false, user_confirmed: false } : m
        ));
        showToast('success', 'Match rejected');
      }
    } catch (e) {
      showToast('error', 'Failed to reject');
    }
  };

  const handleSearchErp = async (queryOverride?: string) => {
    const query = queryOverride || searchErpQuery;
    if (!query.trim()) {
      showToast('warning', 'Please enter a search term');
      return;
    }
    setSearchingErp(true);
    try {
      const response = await fetch(`/api/reconciliation/search-erp?q=${encodeURIComponent(query)}&limit=20`);
      const data = await response.json();
      if (data.success) {
        setSearchErpResults(data.results);
        if (data.results.length === 0) {
          showToast('info', 'No ERP parts found');
        }
      } else {
        showToast('error', data.error || 'Search failed');
      }
    } catch (e) {
      showToast('error', 'Search failed');
    } finally {
      setSearchingErp(false);
    }
  };

  const handleEditUntagged = (item: any) => {
    setEditingUntaggedId(item.id);
    setEditUntaggedValues({
      erp_pp_number: item.erp_pp_number || '',
      erp_description: item.erp_description || item.description || '',
      erp_manufacturer_code: item.erp_manufacturer_code || '',
      erp_manufacturer_name: item.erp_manufacturer_name || '',
      erp_vendor_item_no: item.erp_vendor_item_no || '',
      erp_item_category: item.erp_item_category || '',
      component_type: item.component_type || '',
      quantity: item.quantity || 1,
    });
  };

  const handleSaveUntagged = async () => {
    if (!editingUntaggedId) return;
    
    const currentItem = localUntagged.find(item => item.id === editingUntaggedId);
    const locked = isLockedErpKey(currentItem?.erp_pp_number);
    
    // Update local state with ALL editable fields
    setLocalUntagged(prev => prev.map(item =>
      item.id === editingUntaggedId ? { 
        ...item, 
        erp_pp_number: locked ? item.erp_pp_number : editUntaggedValues.erp_pp_number,
        erp_description: editUntaggedValues.erp_description,
        erp_manufacturer_code: editUntaggedValues.erp_manufacturer_code,
        erp_manufacturer_name: editUntaggedValues.erp_manufacturer_name,
        erp_vendor_item_no: locked ? item.erp_vendor_item_no : editUntaggedValues.erp_vendor_item_no,
        erp_item_category: editUntaggedValues.erp_item_category,
        component_type: editUntaggedValues.component_type,
        quantity: editUntaggedValues.quantity,
      } : item
    ));
    showToast('success', 'Item updated');
    setEditingUntaggedId(null);
    
    // Check for matching lines to apply edits (only if we have ERP data)
    if (editUntaggedValues.erp_pp_number && currentItem) {
      const customerPart = currentItem.customer_part_number;
      const supplierPart = currentItem.supplier_part_number;
      
      // Find ALL matching lines in both matches and untagged (excluding current)
      // Include lines with existing ERP data too - user may want to update all matching
      const matchingInMatches = localMatches.filter(m => 
        m.status !== 'confirmed' && m.status !== 'rejected' &&
        ((customerPart && m.customer_part_number === customerPart) ||
         (supplierPart && (m.customer_supplier_part === supplierPart || m.supplier_part_number === supplierPart)))
      );
      
      const matchingInUntagged = localUntagged.filter(item => 
        item.id !== editingUntaggedId && // Exclude current
        item.status !== 'confirmed' && item.status !== 'rejected' &&
        ((customerPart && item.customer_part_number === customerPart) ||
         (supplierPart && item.supplier_part_number === supplierPart))
      );
      
      const allMatching = [...matchingInMatches, ...matchingInUntagged.map(u => ({ ...u, isUntagged: true }))];
      
      if (allMatching.length > 0) {
        setBatchApplyDialog({
          open: true,
          matchId: editingUntaggedId,
          matchingLines: allMatching,
          matchBy: 'both',
          editValues: { ...editUntaggedValues, _sourceUntagged: true },
        });
      }
    }
    
    setEditUntaggedValues({});
  };

  // Confirm untagged item
  const handleConfirmUntagged = async (itemId: number) => {
    const currentItem = localUntagged.find(item => item.id === itemId);
    
    // Check for matching lines with same part numbers
    if (currentItem?.erp_pp_number) {
      const customerPart = currentItem.customer_part_number;
      const supplierPart = currentItem.supplier_part_number;
      
      const matchingLines = [
        ...localMatches.filter(m => 
          m.erp_pp_number &&
          m.status !== 'confirmed' && m.status !== 'rejected' &&
          ((customerPart && m.customer_part_number === customerPart) ||
           (supplierPart && (m.customer_supplier_part === supplierPart || m.supplier_part_number === supplierPart)))
        ),
        ...localUntagged.filter(item => 
          item.id !== itemId &&
          item.erp_pp_number &&
          item.status !== 'confirmed' && item.status !== 'rejected' &&
          ((customerPart && item.customer_part_number === customerPart) ||
           (supplierPart && item.supplier_part_number === supplierPart))
        ).map(u => ({ ...u, isUntagged: true }))
      ];
      
      if (matchingLines.length > 0) {
        setBatchApplyDialog({
          open: true,
          matchId: itemId,
          matchingLines,
          matchBy: 'both',
          editValues: {
            _action: 'confirm',
            _sourceUntagged: true,
          },
        });
        return;
      }
    }
    
    setLocalUntagged(prev => prev.map(item =>
      item.id === itemId ? { ...item, status: 'confirmed' } : item
    ));
    showToast('success', 'Item confirmed');
  };

  // Reject untagged item
  const handleRejectUntagged = async (itemId: number) => {
    const currentItem = localUntagged.find(item => item.id === itemId);
    
    // Check for matching lines with same part numbers
    if (currentItem) {
      const customerPart = currentItem.customer_part_number;
      const supplierPart = currentItem.supplier_part_number;
      
      const matchingLines = [
        ...localMatches.filter(m => 
          m.status !== 'confirmed' && m.status !== 'rejected' &&
          ((customerPart && m.customer_part_number === customerPart) ||
           (supplierPart && (m.customer_supplier_part === supplierPart || m.supplier_part_number === supplierPart)))
        ),
        ...localUntagged.filter(item => 
          item.id !== itemId &&
          item.status !== 'confirmed' && item.status !== 'rejected' &&
          ((customerPart && item.customer_part_number === customerPart) ||
           (supplierPart && item.supplier_part_number === supplierPart))
        ).map(u => ({ ...u, isUntagged: true }))
      ];
      
      if (matchingLines.length > 0) {
        setBatchApplyDialog({
          open: true,
          matchId: itemId,
          matchingLines,
          matchBy: 'both',
          editValues: {
            _action: 'reject',
            _sourceUntagged: true,
          },
        });
        return;
      }
    }
    
    setLocalUntagged(prev => prev.map(item =>
      item.id === itemId ? { ...item, status: 'rejected' } : item
    ));
    showToast('info', 'Item rejected');
  };

  // Generic ERP lookup by PP# - works for both regular matches and untagged items
  // All changes are stored in editValues/editUntaggedValues - only saved to localMatches on Save click
  const handleLookupErpByPP = async (ppNumber: string, target: 'match' | 'untagged') => {
    if (!ppNumber?.trim()) {
      showToast('warning', 'Please enter an ERP PP# to lookup');
      return;
    }
    
    const searchTerm = ppNumber.trim();
    setSearchingErp(true);
    
    try {
      const response = await fetch(`/api/reconciliation/search-erp?q=${encodeURIComponent(searchTerm)}&limit=10`);
      const data = await response.json();
      
      if (data.success && data.results.length > 0) {
        // Find exact match first
        const exactMatch = data.results.find((r: any) => 
          r.pp_number?.toUpperCase() === searchTerm.toUpperCase() ||
          r.internal_part_number?.toUpperCase() === searchTerm.toUpperCase()
        );
        
        const matchToUse = exactMatch || data.results[0]; // Use exact match or first result
        
        if (matchToUse) {
          if (target === 'match') {
            // Update ALL ERP fields in editValues - they get applied on Save
            setEditValues(prev => ({
              ...prev,
              erp_pp_number: matchToUse.pp_number || matchToUse.internal_part_number || searchTerm,
              erp_description: matchToUse.description || prev.erp_description,
              erp_vendor_item_no: matchToUse.vendor_item_no || matchToUse.external_reference || '',
              erp_manufacturer_code: matchToUse.manufacturer_code || '',
              erp_manufacturer_name: matchToUse.manufacturer_name || '',
              erp_item_category: matchToUse.item_category || '',
            }));
          } else {
            // Update untagged item edit values - include ALL ERP fields
            setEditUntaggedValues(prev => ({
              ...prev,
              erp_pp_number: matchToUse.pp_number || matchToUse.internal_part_number || searchTerm,
              erp_description: matchToUse.description || prev.erp_description,
              erp_manufacturer_code: matchToUse.manufacturer_code || prev.erp_manufacturer_code,
              erp_manufacturer_name: matchToUse.manufacturer_name || prev.erp_manufacturer_name,
              erp_vendor_item_no: matchToUse.vendor_item_no || matchToUse.external_reference || prev.erp_vendor_item_no,
              erp_item_category: matchToUse.item_category || prev.erp_item_category,
            }));
          }
          
          const desc = matchToUse.description?.slice(0, 40) || 'No description';
          showToast('success', `Found: ${matchToUse.pp_number} - ${desc}...`);
        }
      } else {
        showToast('warning', `No ERP part found for "${searchTerm}"`);
      }
    } catch (e) {
      showToast('error', 'ERP lookup failed');
    } finally {
      setSearchingErp(false);
    }
  };

  const handleCancelUntaggedEdit = () => {
    setEditingUntaggedId(null);
    setEditUntaggedValues({});
  };

  const handleAssignErpToUntagged = async (erpPart: any) => {
    if (!searchingForItemId) {
      showToast('error', 'No item selected');
      return;
    }
    // Update local state with ERP data
    setLocalUntagged(prev => prev.map(item =>
      item.id === searchingForItemId ? { 
        ...item, 
        erp_pp_number: erpPart.pp_number || erpPart.internal_part_number,
        erp_description: erpPart.description,
        erp_manufacturer_code: erpPart.manufacturer_code,
        erp_manufacturer_name: erpPart.manufacturer_name,
        erp_vendor_item_no: erpPart.vendor_item_no || erpPart.external_reference,
        erp_item_category: erpPart.item_category
      } : item
    ));
    showToast('success', `Assigned ${erpPart.pp_number || erpPart.internal_part_number} to item`);
    setSearchErpResults([]);
    setSearchErpQuery('');
    setSearchingForItemId(null);
  };

  const showDiagnostic = (match: any) => {
    setDiagnosticMatch(match);
  };

  // Internal export function that uses local state (reflects Confirm/Reject changes)
  const handleInternalExport = () => {
    const allRows: any[] = [];
    
    // Add all matches from local state (includes Confirm/Reject updates)
    localMatches.forEach((m: any) => {
      const pagesStr = m.pages ? (typeof m.pages === 'string' ? m.pages : Array.isArray(m.pages) ? m.pages.join(', ') : '') : '';
      allRows.push({
        'Tag No': m.extracted_tag || '',
        'Tag Origin': m.tag_origin || 'Valid',
        'Type/Category': m.component_type || '',
        'Pages': pagesStr,
        'Customer Part Number': m.customer_part_number || '',
        'Supplier Part Number': m.supplier_part_number || m.customer_supplier_part || '',
        'ERP Number': m.erp_pp_number || '',
        'Vendor Item No': m.erp_vendor_item_no || '',
        'Description': m.erp_description || m.bom_description || m.extracted_description || '',
        'Qty': Number(m.quantity || 1),
        'Category': m.erp_item_category || '',
        'Manufacturer': m.erp_manufacturer_name || '',
        'Vendor Code': m.erp_manufacturer_code || '',
        'Confidence %': Math.round((m.confidence || 0) * 100),
        'Status': m.status || '',
        'User Confirmed': m.user_confirmed ? 'Yes' : 'No',
      });
    });
    
    // Add untagged items from local state
    localUntagged.forEach((item: any) => {
      allRows.push({
        'Tag No': '',
        'Tag Origin': 'Untagged',
        'Type/Category': item.component_type || '',
        'Pages': '',
        'Customer Part Number': item.customer_part_number || '',
        'Supplier Part Number': item.supplier_part_number || '',
        'ERP Number': item.erp_pp_number || '',
        'Vendor Item No': item.erp_vendor_item_no || '',
        'Description': item.erp_description || item.description || '',
        'Qty': Number(item.quantity || 1),
        'Category': item.erp_item_category || '',
        'Manufacturer': item.erp_manufacturer_name || '',
        'Vendor Code': item.erp_manufacturer_code || '',
        'Confidence %': '',
        'Status': 'untagged',
        'User Confirmed': 'No',
      });
    });
    
    if (allRows.length === 0) {
      showToast('warning', 'No data to export');
      return;
    }
    
    // Create CSV
    const headers = Object.keys(allRows[0]);
    const csvContent = [
      headers.join(','),
      ...allRows.map(row => headers.map(h => `"${String(row[h] || '').replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    
    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `reconciliation_export_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    showToast('success', `Exported ${allRows.length} items to CSV`);
  };

  // ERP Export: only user-confirmed items (green lines), grouped by ERP Number, summed qty, concatenated tags
  // Green lines = status === 'confirmed' (user clicked confirm button), NOT auto-accepted
  const handleErpExport = () => {
    // Collect only explicitly user-confirmed matches (green lines with status 'confirmed')
    const confirmedItems = localMatches.filter((m: any) => m.status === 'confirmed');
    
    // Also include confirmed untagged items
    const confirmedUntagged = localUntagged.filter((item: any) => item.status === 'confirmed');
    
    const allConfirmed = [...confirmedItems, ...confirmedUntagged];
    
    if (allConfirmed.length === 0) {
      showToast('warning', 'No confirmed items to export. Only user-confirmed (green) lines are included in ERP Export.');
      return;
    }
    
    // Group by ERP Number: each part number appears once, qty summed, tags concatenated
    const erpGroups: Record<string, {
      erpNumber: string;
      vendorItemNo: string;
      description: string;
      totalQty: number;
      tags: string[];
    }> = {};
    
    allConfirmed.forEach((m: any) => {
      const erpNumber = m.erp_pp_number || '';
      if (!erpNumber || !String(erpNumber).trim()) return;
      
      if (!erpGroups[erpNumber]) {
        erpGroups[erpNumber] = {
          erpNumber,
          vendorItemNo: m.erp_vendor_item_no || '',
          description: m.erp_description || m.bom_description || m.extracted_description || '',
          totalQty: 0,
          tags: [],
        };
      }
      
      erpGroups[erpNumber].totalQty += Number(m.quantity || 1);
      const tag = m.extracted_tag || '';
      if (tag && !erpGroups[erpNumber].tags.includes(tag)) {
        erpGroups[erpNumber].tags.push(tag);
      }
    });
    
    // Round up qty to nearest 0.1
    const roundUpToTenth = (n: number) => Math.ceil(n * 10) / 10;
    
    const erpRows = Object.values(erpGroups).map(group => ({
      'Type': 'Item',
      'No': group.erpNumber,
      'External Ref. No.': group.vendorItemNo,
      'Description': group.description,
      'Qty': roundUpToTenth(group.totalQty),
      'Component Reference': group.tags.sort().join(', '),
    }));
    
    if (erpRows.length === 0) {
      showToast('warning', 'No items with ERP numbers found among confirmed items.');
      return;
    }
    
    // Create CSV
    const headers = Object.keys(erpRows[0]);
    const csvContent = [
      headers.join(','),
      ...erpRows.map(row => headers.map(h => `"${String((row as any)[h] || '').replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    
    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `erp_export_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    showToast('success', `ERP Export: ${erpRows.length} unique parts (${allConfirmed.length} confirmed tags)`);
  };

  const getTagOriginColor = (origin: string) => {
    switch (origin) {
      case 'Valid': return 'success';
      case 'Invalid': return 'error';
      case 'Additional': return 'warning';
      case 'Untagged': return 'default';
      default: return 'default';
    }
  };

  // Dynamic stats - always calculated from local state so they update on changes
  // Note: This must be BEFORE any early returns to comply with React Hooks rules
  // IMPORTANT: Stats logic must match tab filtering logic to avoid items appearing in multiple tabs
  const stats = React.useMemo(() => {
    // Helper: Check if item has ERP match (non-empty erp_pp_number)
    const hasErpMatch = (m: any) => !!(m.erp_pp_number && String(m.erp_pp_number).trim());
    // Define "no match" items: NO ERP PP number (regardless of confidence)
    const isNoMatch = (m: any) => m.status !== 'rejected' && !hasErpMatch(m);
    
    return {
      total_matches: localMatches.length,
      // Auto-accepted: HIGH CONFIDENCE (>=0.5) with ERP match, NOT manually touched
      auto_accepted: localMatches.filter((m: any) => 
        m.status !== 'rejected' && 
        m.confidence >= 0.5 && 
        hasErpMatch(m) &&  // Must have ERP match
        !m.user_confirmed && 
        m.status !== 'confirmed'
      ).length,
      // Manually accepted by user (distinct from auto-accepted)
      confirmed: localMatches.filter((m: any) => 
        m.status !== 'rejected' && (m.user_confirmed || m.status === 'confirmed')
      ).length,
      // Needs Review: confidence < 0.5, WITH ERP match (not in No Match)
      needs_review: localMatches.filter((m: any) => 
        m.status !== 'rejected' && 
        m.confidence < 0.5 && 
        hasErpMatch(m) &&  // Must have ERP match to not be "No Match"
        !m.user_confirmed && 
        m.status !== 'confirmed'
      ).length,
      // No Match: NO ERP PP number (regardless of confidence)
      no_match: localMatches.filter((m: any) => isNoMatch(m)).length,
      rejected: localMatches.filter((m: any) => m.status === 'rejected').length,
      untagged_bom_items: localUntagged.length
    };
  }, [localMatches, localUntagged]);

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', p: 4 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Running reconciliation...</Typography>
      </Box>
    );
  }

  if (!results) {
    return (
      <Box sx={{ textAlign: 'center', p: 4 }}>
        <Button 
          variant="contained" 
          size="large"
          startIcon={<RunIcon />}
          onClick={onRunReconciliation}
        >
          Run Reconciliation
        </Button>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Click to start the reconciliation process using the data from previous steps.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ overflowX: 'auto' }}>
      {/* Action Bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6">Reconciliation Results</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {onRestart && (
            <Button
              variant="outlined"
              color="warning"
              onClick={onRestart}
              size="small"
            >
              Restart
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={onRunReconciliation}
            size="small"
          >
            Re-run
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleInternalExport}
            size="small"
            color="success"
          >
            Export to Excel
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={handleErpExport}
            size="small"
          >
            ERP Export
          </Button>
        </Box>
      </Box>

      {/* Stats */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Chip icon={<TagIcon />} label={`${stats.total_matches} Tags`} />
        <Chip icon={<AutoIcon />} label={`${stats.auto_accepted} Auto-Accepted`} color="success" variant="outlined" />
        {stats.confirmed > 0 && (
          <Chip icon={<CheckIcon />} label={`${stats.confirmed} Accepted`} sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }} />
        )}
        <Chip icon={<ReviewIcon />} label={`${stats.needs_review} Review`} color="warning" />
        <Chip icon={<NoMatchIcon />} label={`${stats.no_match} No Match`} color="error" />
        {stats.rejected > 0 && (
          <Chip icon={<CloseIcon />} label={`${stats.rejected} Rejected`} sx={{ bgcolor: '#ffebee', color: '#c62828' }} />
        )}
        <Chip label={`${stats.untagged_bom_items} Untagged`} variant="outlined" />
      </Box>

      {/* Search and Sort Controls */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search by tag, supplier part, customer part, ERP#..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: 350 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchQuery('')}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        
        {/* Sort Controls */}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Sort on</InputLabel>
          <Select
            value={sortField}
            label="Sort on"
            onChange={(e) => setSortField(e.target.value)}
          >
            <MenuItem value="extracted_tag">Tag</MenuItem>
            <MenuItem value="component_type">Type</MenuItem>
            <MenuItem value="customer_part_number">Customer Part#</MenuItem>
            <MenuItem value="customer_supplier_part">Supplier Part#</MenuItem>
            <MenuItem value="erp_pp_number">ERP PP#</MenuItem>
            <MenuItem value="erp_description">Description</MenuItem>
            <MenuItem value="confidence">Confidence</MenuItem>
            <MenuItem value="status">Status</MenuItem>
          </Select>
        </FormControl>
        <Button
          size="small"
          variant="outlined"
          onClick={() => setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')}
          sx={{ minWidth: 40, px: 1 }}
        >
          {sortDirection === 'asc' ? '↑' : '↓'}
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={() => setTableLayout(prev => prev === 'fit' ? 'wide' : 'fit')}
        >
          {tableLayout === 'fit' ? 'Wide columns' : 'Fit columns'}
        </Button>
        
        {searchQuery && (
          <Typography variant="body2" color="text.secondary">
            {activeTab < 3 
              ? `${filteredMatches.length} results found`
              : `${filteredUntagged.length} results found`
            }
          </Typography>
        )}
      </Box>
      
      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
        <Tab label={`High Confidence (${stats.auto_accepted})`} />
        <Tab label={`Needs Review (${stats.needs_review})`} />
        <Tab label={`No Match (${stats.no_match})`} />
        <Tab label={`Untagged BOM (${stats.untagged_bom_items})`} />
      </Tabs>
      
      {/* Results Table */}
      <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 400px)', minHeight: 300, overflow: 'auto' }}>
        <Table size="small" stickyHeader sx={tableLayout === 'fit' ? { width: 'max-content', minWidth: '100%' } : { minWidth: 1650 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 80 }}>Tag</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 80 }}>Origin</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 100 }}>Type</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 100 }}>Customer Part#</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 100 }}>Supplier Part#</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 100 }}>ERP PP#</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 120 }}>Vendor Item No</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 150 }}>Description</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 60 }}>Qty</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 100 }}>Category</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 100 }}>Manufacturer</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 80 }}>Vendor Code</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 90 }}>Confidence</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 220, position: 'sticky', right: 0, bgcolor: 'background.paper', zIndex: 3 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Use memoized filteredMatches for proper React state reactivity */}
            {activeTab < 3 && filteredMatches.map((match: any) => {
                const tone = reconciliationRowTone(match.erp_pp_number, match.status);
                return (
                <TableRow 
                  key={match.id}
                  sx={{ ...tone, transition: 'all 0.3s ease' }}
                >
                  <TableCell>
                    <Tooltip 
                      title={
                        <Box sx={{ p: 0.5 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', borderBottom: '1px solid rgba(255,255,255,0.3)', pb: 0.5, mb: 0.5 }}>
                            BOM Line Detail
                          </Typography>
                          <Typography variant="body2"><strong>Tag:</strong> {match.extracted_tag}</Typography>
                          <Typography variant="body2"><strong>BOM Description:</strong> {match.bom_description || match.customer_description || '-'}</Typography>
                          <Typography variant="body2"><strong>Customer Part#:</strong> {match.customer_part_number || '-'}</Typography>
                          <Typography variant="body2"><strong>Supplier Part#:</strong> {match.supplier_part_number || match.customer_supplier_part || '-'}</Typography>
                          <Typography variant="body2"><strong>Qty:</strong> {match.quantity || 1}</Typography>
                          {match.raw_text && (
                            <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic', opacity: 0.9 }}>
                              <strong>Raw:</strong> {match.raw_text.substring(0, 100)}{match.raw_text.length > 100 ? '...' : ''}
                            </Typography>
                          )}
                        </Box>
                      }
                      arrow
                      placement="right"
                    >
                      <Box sx={{ cursor: 'help' }}>
                        <strong>{match.extracted_tag}</strong>
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      size="small" 
                      label={match.tag_origin || 'Valid'} 
                      color={getTagOriginColor(match.tag_origin || 'Valid') as any}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {editingId === match.id ? (
                      <FormControl size="small" sx={{ minWidth: 100 }}>
                        <Select
                          value={editValues.component_type || ''}
                          onChange={(e) => setEditValues({ ...editValues, component_type: e.target.value })}
                          displayEmpty
                          sx={{ fontSize: '0.8rem' }}
                        >
                          <MenuItem value=""><em>Select...</em></MenuItem>
                          {componentTypes.map(t => (
                            <MenuItem key={t.name} value={t.name}>{t.display_name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <Chip size="small" label={match.component_type || '-'} />
                    )}
                  </TableCell>
                  <TableCell>{match.customer_part_number || '-'}</TableCell>
                  <TableCell>
                    <Tooltip title={match.supplier_part_number || match.customer_supplier_part || '-'}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', maxWidth: 100 }} noWrap>
                        {match.supplier_part_number || match.customer_supplier_part || '-'}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {editingId === match.id && !isLockedErpKey(match.erp_pp_number) ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TextField
                          size="small"
                          value={editValues.erp_pp_number}
                          onChange={(e) => setEditValues({ ...editValues, erp_pp_number: e.target.value })}
                          sx={{ width: 90 }}
                          placeholder="PN..."
                        />
                        <Tooltip title="Lookup ERP by PP#">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleLookupErpByPP(editValues.erp_pp_number, 'match')}
                            disabled={searchingErp || !editValues.erp_pp_number}
                          >
                            {searchingErp ? <CircularProgress size={16} /> : <SearchIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : (
                      <Tooltip title={isLockedErpKey(match.erp_pp_number) ? 'Primary key — cannot be edited' : ''}>
                        <code>{match.erp_pp_number || '-'}</code>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    {/* Show editValues when editing, otherwise match data */}
                    <Tooltip title={editingId === match.id 
                      ? (editValues.erp_vendor_item_no || match.erp_vendor_item_no || '-')
                      : (match.erp_vendor_item_no || '-')}>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 120, cursor: 'help' }}>
                        {editingId === match.id 
                          ? (editValues.erp_vendor_item_no || match.erp_vendor_item_no || '-')
                          : (match.erp_vendor_item_no || '-')}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {editingId === match.id ? (
                      <TextField
                        size="small"
                        value={editValues.erp_description}
                        onChange={(e) => setEditValues({ ...editValues, erp_description: e.target.value })}
                        sx={{ width: 150 }}
                      />
                    ) : (
                      <Tooltip title={[match.erp_description || match.bom_description || match.extracted_description, match.erp_description_2].filter(Boolean).join(' — ') || '-'}>
                        <Box>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 150, cursor: 'help' }}>
                            {match.erp_description || match.bom_description || match.extracted_description || '-'}
                          </Typography>
                          {match.erp_description_2 && (
                            <Typography variant="body2" noWrap sx={{ maxWidth: 150, cursor: 'help' }}>
                              {match.erp_description_2}
                            </Typography>
                          )}
                        </Box>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    {editingId === match.id ? (
                      <TextField
                        size="small"
                        type="number"
                        value={editValues.quantity}
                        onChange={(e) => setEditValues({ ...editValues, quantity: e.target.value })}
                        sx={{ width: 60 }}
                      />
                    ) : (
                      Number(match.quantity || 1)
                    )}
                  </TableCell>
                  <TableCell>
                    {/* Show editValues category when editing */}
                    {editingId === match.id ? (
                      (editValues.erp_item_category || match.erp_item_category) && 
                      <Chip size="small" label={editValues.erp_item_category || match.erp_item_category} variant="outlined" sx={{ bgcolor: editValues.erp_item_category ? 'action.selected' : 'transparent' }} />
                    ) : (
                      match.erp_item_category && <Chip size="small" label={match.erp_item_category} variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    {/* Manufacturer Name */}
                    <Tooltip title={editingId === match.id 
                      ? (editValues.erp_manufacturer_name || match.erp_manufacturer_name || '-')
                      : (match.erp_manufacturer_name || '-')}>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 100, cursor: 'help' }}>
                        {editingId === match.id 
                          ? (editValues.erp_manufacturer_name || match.erp_manufacturer_name || '-')
                          : (match.erp_manufacturer_name || '-')}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {/* Manufacturer Code (from ERP "Manufacture" field) */}
                    {editingId === match.id ? (
                      <Tooltip title={`Manufacturer Code: ${editValues.erp_manufacturer_code || match.erp_manufacturer_code || '-'}`}>
                        <Box>
                          {(editValues.erp_manufacturer_code || match.erp_manufacturer_code) && (
                            <Chip 
                              size="small" 
                              label={editValues.erp_manufacturer_code || match.erp_manufacturer_code} 
                              variant="outlined"
                              sx={{ bgcolor: editValues.erp_manufacturer_code ? 'action.selected' : 'transparent' }}
                            />
                          )}
                          {!editValues.erp_manufacturer_code && !match.erp_manufacturer_code && (
                            <Typography variant="caption" color="text.secondary">-</Typography>
                          )}
                        </Box>
                      </Tooltip>
                    ) : (
                      <Tooltip title={`Manufacturer Code: ${match.erp_manufacturer_code || '-'}`}>
                        <Box>
                          {match.erp_manufacturer_code && (
                            <Chip size="small" label={match.erp_manufacturer_code} variant="outlined" />
                          )}
                          {!match.erp_manufacturer_code && (
                            <Typography variant="caption" color="text.secondary">-</Typography>
                          )}
                        </Box>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Click for match details">
                      <Box 
                        sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
                        onClick={() => showDiagnostic(match)}
                      >
                        <LinearProgress 
                          variant="determinate" 
                          value={match.confidence * 100}
                          color={match.confidence >= 0.5 ? 'success' : match.confidence > 0 ? 'warning' : 'error'}
                          sx={{ width: 50, height: 6, borderRadius: 3 }}
                        />
                        <Typography variant="caption">{Math.round(match.confidence * 100)}%</Typography>
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={stickyActionSx(tone.bgcolor)}>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      {/* Status Indicator */}
                      {match.status === 'confirmed' && (
                        <Chip 
                          size="small" 
                          label="✓ Confirmed" 
                          color="success" 
                          sx={{ mr: 1, fontWeight: 'bold' }}
                        />
                      )}
                      {match.status === 'rejected' && (
                        <Chip 
                          size="small" 
                          label="✗ Rejected" 
                          color="error" 
                          sx={{ mr: 1, fontWeight: 'bold' }}
                        />
                      )}
                      
                      {editingId === match.id ? (
                        <>
                          <Tooltip title="Save changes">
                            <IconButton size="small" color="success" onClick={() => handleSaveEdit(match.id)}>
                              <SaveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancel editing">
                            <IconButton size="small" color="error" onClick={() => { setEditingId(null); setEditValues({}); }}>
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <>
                          <Tooltip title="Find ERP Matches">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleFindErpMatches(match)}
                            >
                              <SearchIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleEdit(match)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={match.status === 'confirmed' ? "Already Confirmed" : "Confirm this match"}>
                            <IconButton 
                              size="small" 
                              color={match.status === 'confirmed' ? "success" : "default"}
                              onClick={() => handleConfirm(match.id)}
                              disabled={match.status === 'confirmed'}
                            >
                              <CheckIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={match.status === 'rejected' ? "Already Rejected" : "Reject this match"}>
                            <IconButton 
                              size="small" 
                              color={match.status === 'rejected' ? "error" : "default"}
                              onClick={() => handleReject(match.id)}
                              disabled={match.status === 'rejected'}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {/* Flag as NEW ITEM - for parts not in ERP */}
                          <Tooltip title="Flag as NEW ITEM (not in ERP - needs to be added)">
                            <IconButton 
                              size="small" 
                              sx={{ 
                                color: match.erp_pp_number === 'NEW ITEM' ? '#1976d2' : '#90caf9',
                                '&:hover': { bgcolor: '#e3f2fd' }
                              }}
                              onClick={() => handleFlagAsNewItem(match.id)}
                              disabled={match.erp_pp_number === 'NEW ITEM'}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
                );
              })}
            
            {/* Untagged Items Tab - Same structure as other tabs */}
            {activeTab === 3 && filteredUntagged.map((item: any) => {
              const tone = reconciliationRowTone(item.erp_pp_number, item.status);
              return (
              <TableRow 
                key={item.id}
                sx={tone}
              >
                {/* Tag */}
                <TableCell>
                  <Tooltip 
                    title={
                      <Box sx={{ p: 0.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>BOM Line Detail</Typography>
                        <Typography variant="body2">BOM Description: {item.description || '-'}</Typography>
                        <Typography variant="body2">Customer Part#: {item.customer_part_number || '-'}</Typography>
                        <Typography variant="body2">Supplier Part#: {item.supplier_part_number || '-'}</Typography>
                        <Typography variant="body2">Qty: {item.quantity || 1}</Typography>
                      </Box>
                    }
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help' }}>(untagged)</Typography>
                  </Tooltip>
                </TableCell>
                {/* Origin */}
                <TableCell>
                  <Tooltip title="Item from uploaded BOM file - not matched to a PDF tag">
                    <Chip size="small" label="Untagged" variant="outlined" color="default" sx={{ cursor: 'help' }} />
                  </Tooltip>
                </TableCell>
                {/* Type - Editable dropdown */}
                <TableCell>
                  {editingUntaggedId === item.id ? (
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                      <Select
                        value={editUntaggedValues.component_type || ''}
                        onChange={(e) => setEditUntaggedValues({ ...editUntaggedValues, component_type: e.target.value })}
                        displayEmpty
                        sx={{ fontSize: '0.8rem' }}
                      >
                        <MenuItem value=""><em>Select...</em></MenuItem>
                        {componentTypes.map(t => (
                          <MenuItem key={t.name} value={t.name}>{t.display_name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    item.component_type || '-'
                  )}
                </TableCell>
                {/* Customer Part# */}
                <TableCell>{item.customer_part_number || '-'}</TableCell>
                {/* Supplier Part# */}
                <TableCell>{item.supplier_part_number || '-'}</TableCell>
                {/* ERP PP# - With search icon */}
                <TableCell>
                  {editingUntaggedId === item.id && !isLockedErpKey(item.erp_pp_number) ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <TextField
                        size="small"
                        value={editUntaggedValues.erp_pp_number}
                        onChange={(e) => setEditUntaggedValues({ ...editUntaggedValues, erp_pp_number: e.target.value })}
                        sx={{ width: 90 }}
                        placeholder="PN..."
                      />
                      <Tooltip title="Lookup ERP by PP#">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleLookupErpByPP(editUntaggedValues.erp_pp_number, 'untagged')}
                          disabled={searchingErp || !editUntaggedValues.erp_pp_number}
                        >
                          {searchingErp ? <CircularProgress size={16} /> : <SearchIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ) : (
                    <Tooltip title={isLockedErpKey(item.erp_pp_number) ? 'Primary key — cannot be edited' : ''}>
                      <code>{item.erp_pp_number || '-'}</code>
                    </Tooltip>
                  )}
                </TableCell>
                {/* Vendor Item No - Shows ERP lookup value when editing */}
                <TableCell>
                  <Tooltip title={editingUntaggedId === item.id 
                    ? (editUntaggedValues.erp_vendor_item_no || item.erp_vendor_item_no || '-')
                    : (item.erp_vendor_item_no || '-')}>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 120, cursor: 'help' }}>
                      {editingUntaggedId === item.id 
                        ? (editUntaggedValues.erp_vendor_item_no || item.erp_vendor_item_no || '-')
                        : (item.erp_vendor_item_no || '-')}
                    </Typography>
                  </Tooltip>
                </TableCell>
                {/* Description - Editable */}
                <TableCell>
                  {editingUntaggedId === item.id ? (
                    <TextField
                      size="small"
                      value={editUntaggedValues.erp_description}
                      onChange={(e) => setEditUntaggedValues({ ...editUntaggedValues, erp_description: e.target.value })}
                      sx={{ width: 150 }}
                    />
                  ) : (
                    <Tooltip title={[item.erp_description || item.description, item.erp_description_2].filter(Boolean).join(' — ') || '-'}>
                      <Box>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 150, cursor: 'help' }}>
                        {item.erp_description || item.description || '-'}
                      </Typography>
                      {item.erp_description_2 && (
                        <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                          {item.erp_description_2}
                        </Typography>
                      )}
                      </Box>
                    </Tooltip>
                  )}
                </TableCell>
                {/* Qty - Editable */}
                <TableCell>
                  {editingUntaggedId === item.id ? (
                    <TextField
                      size="small"
                      type="number"
                      value={editUntaggedValues.quantity}
                      onChange={(e) => setEditUntaggedValues({ ...editUntaggedValues, quantity: Number(e.target.value) })}
                      sx={{ width: 60 }}
                    />
                  ) : (
                    Number(item.quantity || 1)
                  )}
                </TableCell>
                {/* Category - Shows ERP lookup value when editing */}
                <TableCell>
                  {editingUntaggedId === item.id ? (
                    (editUntaggedValues.erp_item_category || item.erp_item_category) && 
                    <Chip size="small" label={editUntaggedValues.erp_item_category || item.erp_item_category} variant="outlined" sx={{ bgcolor: editUntaggedValues.erp_item_category ? 'action.selected' : 'transparent' }} />
                  ) : (
                    item.erp_item_category && <Chip size="small" label={item.erp_item_category} variant="outlined" />
                  )}
                  {!editingUntaggedId && !item.erp_item_category && '-'}
                </TableCell>
                {/* Manufacturer Name */}
                <TableCell>
                  <Tooltip title={editingUntaggedId === item.id 
                    ? (editUntaggedValues.erp_manufacturer_name || item.erp_manufacturer_name || '-')
                    : (item.erp_manufacturer_name || '-')}>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 100, cursor: 'help' }}>
                      {editingUntaggedId === item.id 
                        ? (editUntaggedValues.erp_manufacturer_name || item.erp_manufacturer_name || '-')
                        : (item.erp_manufacturer_name || '-')}
                    </Typography>
                  </Tooltip>
                </TableCell>
                {/* Manufacturer Code */}
                <TableCell>
                  {editingUntaggedId === item.id ? (
                    (editUntaggedValues.erp_manufacturer_code || item.erp_manufacturer_code) && (
                      <Chip 
                        size="small" 
                        label={editUntaggedValues.erp_manufacturer_code || item.erp_manufacturer_code} 
                        variant="outlined"
                        sx={{ bgcolor: editUntaggedValues.erp_manufacturer_code ? 'action.selected' : 'transparent' }}
                      />
                    )
                  ) : (
                    item.erp_manufacturer_code && (
                      <Chip size="small" label={item.erp_manufacturer_code} variant="outlined" />
                    )
                  )}
                  {editingUntaggedId !== item.id && !item.erp_manufacturer_code && '-'}
                </TableCell>
                {/* Confidence - Always 0% for untagged */}
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={0}
                      color="error"
                      sx={{ width: 50, height: 6, borderRadius: 3 }}
                    />
                    <Typography variant="caption">0%</Typography>
                  </Box>
                </TableCell>
                {/* Actions - same order and labels as the other tabs */}
                <TableCell sx={stickyActionSx(tone.bgcolor)}>
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    {item.status === 'confirmed' && (
                      <Chip size="small" label="✓ Confirmed" color="success" sx={{ mr: 1, fontWeight: 'bold' }} />
                    )}
                    {item.status === 'rejected' && (
                      <Chip size="small" label="✗ Rejected" color="error" sx={{ mr: 1, fontWeight: 'bold' }} />
                    )}
                    
                    {editingUntaggedId === item.id ? (
                      <>
                        <Tooltip title="Save changes">
                          <IconButton size="small" color="success" onClick={handleSaveUntagged}>
                            <SaveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel editing">
                          <IconButton size="small" color="error" onClick={handleCancelUntaggedEdit}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <>
                        <Tooltip title="Find ERP Matches">
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleFindErpMatchesForUntagged(item)}
                          >
                            <SearchIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleEditUntagged(item)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={item.status === 'confirmed' ? "Already Confirmed" : "Confirm this match"}>
                          <IconButton 
                            size="small" 
                            color={item.status === 'confirmed' ? "success" : "default"}
                            onClick={() => handleConfirmUntagged(item.id)}
                            disabled={item.status === 'confirmed'}
                          >
                            <CheckIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={item.status === 'rejected' ? "Already Rejected" : "Reject this match"}>
                          <IconButton 
                            size="small" 
                            color={item.status === 'rejected' ? "error" : "default"}
                            onClick={() => handleRejectUntagged(item.id)}
                            disabled={item.status === 'rejected'}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Flag as NEW ITEM (not in ERP - needs to be added)">
                          <IconButton
                            size="small"
                            sx={{
                              color: item.erp_pp_number === 'NEW ITEM' ? '#1976d2' : '#90caf9',
                              '&:hover': { bgcolor: '#e3f2fd' }
                            }}
                            onClick={() => handleFlagUntaggedAsNewItem(item.id)}
                            disabled={item.erp_pp_number === 'NEW ITEM'}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ERP Search Dialog for Untagged Items */}
      {searchErpResults.length > 0 && (
        <Paper sx={{ p: 2, mt: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'primary.main' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2">
              ERP Search Results for "{searchErpQuery}" ({searchErpResults.length} found)
            </Typography>
            <Button size="small" onClick={() => { setSearchErpResults([]); setSearchingForItemId(null); }}>Close</Button>
          </Box>
          <TableContainer sx={{ maxHeight: 200 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>PP#</TableCell>
                  <TableCell>Vendor Item</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Manufacturer</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {searchErpResults.map((erp: any) => (
                  <TableRow key={erp.id}>
                    <TableCell><code>{erp.pp_number}</code></TableCell>
                    <TableCell>{erp.vendor_item_no || '-'}</TableCell>
                    <TableCell>{erp.description}</TableCell>
                    <TableCell>{erp.manufacturer_name || erp.manufacturer_code}</TableCell>
                    <TableCell>
                      <Button size="small" variant="contained" color="success" onClick={() => handleAssignErpToUntagged(erp)}>
                        Use This
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Match Diagnostic Dialog */}
      <Dialog open={!!diagnosticMatch} onClose={() => setDiagnosticMatch(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Match Diagnostic</DialogTitle>
        <DialogContent>
          {diagnosticMatch && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>Tag: {diagnosticMatch.extracted_tag}</Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Confidence:</strong> {Math.round(diagnosticMatch.confidence * 100)}%
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Status:</strong> {diagnosticMatch.status}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>Match Sources:</Typography>
              {diagnosticMatch.match_sources && (
                <Box component="ul" sx={{ pl: 2 }}>
                  {Object.entries(diagnosticMatch.match_sources).map(([key, value]) => (
                    <li key={key}>
                      <Typography variant="body2">
                        {key}: {value ? '✅ Yes' : '❌ No'}
                      </Typography>
                    </li>
                  ))}
                </Box>
              )}
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>ERP Match Details:</Typography>
              <Typography variant="body2">PP#: {diagnosticMatch.erp_pp_number || 'Not matched'}</Typography>
              <Typography variant="body2">Manufacturer: {diagnosticMatch.erp_manufacturer_code || diagnosticMatch.erp_manufacturer_name || '-'}</Typography>
              <Typography variant="body2">Category: {diagnosticMatch.erp_item_category || '-'}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiagnosticMatch(null)}>Close</Button>
        </DialogActions>
      </Dialog>
      
      {/* Batch Apply Dialog */}
      <Dialog 
        open={batchApplyDialog.open} 
        onClose={() => setBatchApplyDialog(prev => ({ ...prev, open: false }))}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoIcon color={batchApplyDialog.editValues._action === 'reject' ? 'error' : 'warning'} />
          {batchApplyDialog.editValues._action === 'reject' ? 'Reject Matching Lines?' : 
           batchApplyDialog.editValues._action === 'confirm' ? 'Confirm Matching Lines?' : 
           batchApplyDialog.editValues._action === 'new_item' ? 'Flag the same customer part as NEW ITEM?' :
           'Apply to Matching Lines?'}
        </DialogTitle>
        <DialogContent>
          <Alert 
            severity={batchApplyDialog.editValues._action === 'reject' ? 'warning' : 'info'} 
            sx={{ mb: 2 }}
          >
            This part is on <strong>{batchApplyDialog.matchingLines.length}</strong> other lines in this page.
            <br />
            Would you like to <strong>{batchApplyDialog.editValues._action === 'reject' ? 'Reject' : batchApplyDialog.editValues._action === 'confirm' ? 'Confirm' : batchApplyDialog.editValues._action === 'new_item' ? 'Flag' : 'Apply to'} all</strong>, or just this one?
          </Alert>
          
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Other lines with same part number:</Typography>
          <Box sx={{ maxHeight: 150, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1, bgcolor: '#fafafa' }}>
            {batchApplyDialog.matchingLines.map((line, idx) => (
              <Box key={idx} sx={{ py: 0.5, borderBottom: idx < batchApplyDialog.matchingLines.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                <Typography variant="body2">
                  <strong>{line.extracted_tag}</strong> - {line.customer_part_number || line.customer_supplier_part || 'No part#'}
                </Typography>
              </Box>
            ))}
          </Box>
          
          {/* Show ERP data only for non-confirm/reject actions */}
          {batchApplyDialog.editValues._action !== 'confirm' && batchApplyDialog.editValues._action !== 'reject' && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: '#e8f5e9', borderRadius: 1, border: '1px solid #c8e6c9' }}>
              <Typography variant="subtitle2" color="success.dark">ERP data to apply:</Typography>
              <Typography variant="body2"><strong>PP#:</strong> {batchApplyDialog.editValues.erp_pp_number || '-'}</Typography>
              <Typography variant="body2"><strong>Description:</strong> {batchApplyDialog.editValues.erp_description || '-'}</Typography>
              <Typography variant="body2"><strong>Vendor Code:</strong> {batchApplyDialog.editValues.erp_manufacturer_code || '-'}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button 
            variant="contained"
            color={batchApplyDialog.editValues._action === 'reject' ? 'error' : 'success'}
            onClick={handleBatchApply}
          >
            {batchApplyDialog.editValues._action === 'reject' ? 'Reject All' : 
             batchApplyDialog.editValues._action === 'confirm' ? 'Confirm All' : 
             batchApplyDialog.editValues._action === 'new_item' ? 'Flag all' :
             'Apply to All'}
          </Button>
          <Button 
            variant="outlined"
            onClick={handleJustThisOne}
          >
            Just This One
          </Button>
          <Button 
            color="inherit"
            onClick={() => setBatchApplyDialog(prev => ({ ...prev, open: false }))}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Find ERP Matches Dialog */}
      <Dialog 
        open={findMatchesDialog.open} 
        onClose={() => setFindMatchesDialog(prev => ({ ...prev, open: false }))}
        maxWidth="lg" 
        fullWidth
        PaperProps={{ sx: { minWidth: '900px' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SearchIcon color="primary" />
          Find ERP Matches
          {findMatchesDialog.match && (
            <Chip 
              size="small" 
              label={findMatchesDialog.match.extracted_tag} 
              color="primary" 
              sx={{ ml: 1 }} 
            />
          )}
        </DialogTitle>
        <DialogContent>
          {findMatchesDialog.match && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">Searching for matches based on:</Typography>
              <Typography variant="body2">
                <strong>Customer Part#:</strong> {findMatchesDialog.match.customer_part_number || '-'}
              </Typography>
              <Typography variant="body2">
                <strong>Supplier Part#:</strong> {findMatchesDialog.match.customer_supplier_part || '-'}
              </Typography>
            </Box>
          )}
          
          {findMatchesDialog.loading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>Searching ERP database...</Typography>
            </Box>
          ) : findMatchesDialog.erpCandidates.length === 0 ? (
            <Alert severity="info">No potential matches found in ERP database.</Alert>
          ) : (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Found {findMatchesDialog.erpCandidates.length} potential match(es) - click to apply:
              </Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', width: '100px' }}>PP#</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '110px' }}>Vendor Item</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '90px' }}>Cust No</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '180px' }}>Description</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '90px' }}>Mfr</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '70px' }}>Category</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '140px' }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {findMatchesDialog.erpCandidates.map((erp, idx) => (
                      <TableRow 
                        key={idx}
                        hover
                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#e3f2fd' } }}
                      >
                        <TableCell sx={{ overflow: 'hidden' }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold', fontSize: '0.75rem' }} noWrap>
                            {erp.pp_number || erp.internal_part_number}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ overflow: 'hidden' }}>
                          <Tooltip title={erp.vendor_item_no || erp.external_reference || '-'}>
                            <Typography variant="body2" noWrap sx={{ fontSize: '0.75rem' }}>
                              {erp.vendor_item_no || erp.external_reference || '-'}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ overflow: 'hidden' }}>
                          <Typography variant="body2" noWrap sx={{ fontSize: '0.75rem' }}>
                            {erp.customer_no || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ overflow: 'hidden' }}>
                          <Tooltip title={[erp.description, erp.description_2].filter(Boolean).join(' — ') || '-'}>
                            <Box>
                              <Typography variant="body2" sx={{ fontSize: '0.75rem', cursor: 'help' }}>
                                {erp.description || '-'}
                              </Typography>
                              {erp.description_2 && (
                                <Typography variant="body2" sx={{ fontSize: '0.75rem', cursor: 'help' }}>
                                  {erp.description_2}
                                </Typography>
                              )}
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ overflow: 'hidden' }}>
                          <Typography variant="body2" noWrap sx={{ fontSize: '0.75rem' }}>
                            {erp.manufacturer_name || erp.manufacturer_code || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ overflow: 'hidden' }}>
                          <Typography variant="body2" noWrap sx={{ fontSize: '0.75rem' }}>
                            {erp.item_category || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexDirection: 'row', flexWrap: 'wrap' }}>
                            <Button 
                              size="small" 
                              variant="contained" 
                              color="success"
                              onClick={() => handleApplyErpMatch(erp)}
                              sx={{ minWidth: 60, fontSize: '0.7rem', py: 0.3 }}
                            >
                              Apply
                            </Button>
                            {(() => {
                              // Calculate ALL matching lines count (including those with existing ERP data)
                              const match = findMatchesDialog.match;
                              if (!match) return null;
                              const customerPart = match.customer_part_number;
                              const supplierPart = match.customer_supplier_part || match.supplier_part_number;
                              
                              // Count ALL items with matching part numbers (can update/overwrite)
                              const matchCount = localMatches.filter(m => 
                                m.status !== 'rejected' &&
                                ((customerPart && m.customer_part_number === customerPart) ||
                                 (supplierPart && (m.customer_supplier_part === supplierPart || m.supplier_part_number === supplierPart)))
                              ).length + localUntagged.filter(item => 
                                item.status !== 'rejected' &&
                                ((customerPart && item.customer_part_number === customerPart) ||
                                 (supplierPart && item.supplier_part_number === supplierPart))
                              ).length;
                              
                              if (matchCount <= 1) return null; // Only show if more than 1 matching line
                              
                              return (
                                <Button 
                                  size="small" 
                                  variant="contained" 
                                  color="success"
                                  onClick={() => handleApplyErpMatchToAll(erp)}
                                  sx={{ minWidth: 60, fontSize: '0.65rem', py: 0.3 }}
                                >
                                  All ({matchCount})
                                </Button>
                              );
                            })()}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFindMatchesDialog(prev => ({ ...prev, open: false }))}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ============================================================================
// Main Reconciliation View
// ============================================================================

const ReconciliationView: React.FC = () => {
  const { showToast, setLoading } = useBOM();
  
  // View mode: 'wizard' or 'history' - will be set after checking for existing reconciliations
  const [viewMode, setViewMode] = useState<'wizard' | 'history' | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  // Wizard state
  const [activeStep, setActiveStep] = useState(0);
  
  // Selection state
  const [folders, setFolders] = useState<CustomerFolder[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState('');
  const [selectedFolderName, setSelectedFolderName] = useState('');
  const [jobs, setJobs] = useState<any[]>([]);
  const [selectedJobId, setSelectedJobId] = useState('');
  const [selectedPdfName, setSelectedPdfName] = useState('');
  
  // Data state
  const [extractedTags, setExtractedTags] = useState<ExtractedTag[]>([]);
  const [bomItems, setBomItems] = useState<BOMItem[]>([]);
  const [crossRefs, setCrossRefs] = useState<any[]>([]);
  const [results, setResults] = useState<any>(null);
  
  // History state
  const [reconciliationHistory, setReconciliationHistory] = useState<any[]>([]);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState<any>(null);
  const [loadingViewJobId, setLoadingViewJobId] = useState<string | null>(null);
  
  // Loading states
  const [tagsLoading, setTagsLoading] = useState(false);
  const [reconciliationLoading, setReconciliationLoading] = useState(false);

  const cleanPdfFilename = (filename: string): string => {
    if (!filename) return '';
    return filename.replace(/^[a-f0-9]{32}_/i, '');
  };

  // Load folders
  useEffect(() => {
    const loadFolders = async () => {
      try {
        const data = await bomApi.listFolders();
        setFolders(data);
      } catch (e) {}
    };
    loadFolders();
  }, []);

  // Load jobs when folder changes
  useEffect(() => {
    const loadJobs = async () => {
      try {
        const response = await bomApi.getJobs();
        let folderJobs = selectedFolderId 
          ? response.filter((j: any) => j.folder_id === selectedFolderId)
          : response;
        
        const seenPdfs = new Map<string, any>();
        folderJobs.forEach((job: any) => {
          const cleanName = cleanPdfFilename(job.pdf_filename);
          if (!seenPdfs.has(cleanName)) {
            seenPdfs.set(cleanName, { ...job, cleanPdfName: cleanName });
          }
        });
        
        setJobs(Array.from(seenPdfs.values()));
      } catch (e) {}
    };
    loadJobs();
  }, [selectedFolderId]);

  // Load reconciliation history on mount and when switching to history view
  useEffect(() => {
    const loadHistory = async () => {
      // Load all jobs that have reconciliation results
      try {
        const response = await bomApi.getJobs();
        const jobsToCheck = response.slice(0, 20);
        
        // OPTIMIZATION: Use lightweight stats endpoint for history list (much faster)
        // Only load full reconciliation data when user clicks "View"
        const reconciliationPromises = jobsToCheck.map(async (job: any) => {
          try {
            // Use lightweight stats endpoint instead of full results
            const stats = await bomApi.getReconciliationStats(job.job_id);
            if (stats.success && stats.stats && stats.stats.total_matches > 0) {
              return {
                ...job,
                cleanPdfName: cleanPdfFilename(job.pdf_filename),
                reconciliation: {
                  stats: stats.stats,
                  // Full data will be loaded when user clicks "View"
                  _fullDataLoaded: false
                }
              };
            }
            return null;
          } catch (e) {
            return null;
          }
        });
        
        // Wait for all promises to resolve in parallel
        const results = await Promise.all(reconciliationPromises);
        const jobsWithReconciliation = results.filter((job: any) => job !== null);
        
        setReconciliationHistory(jobsWithReconciliation);
        
        // Set default view mode based on whether there are existing reconciliations
        if (viewMode === null) {
          setViewMode(jobsWithReconciliation.length > 0 ? 'history' : 'wizard');
        }
      } catch (e) {
        // On error, default to wizard
        if (viewMode === null) {
          setViewMode('wizard');
        }
      }
    };
    
    // Load on mount (viewMode === null) or when switching to history
    if (viewMode === null || viewMode === 'history') {
      loadHistory();
    }
  }, [viewMode]);

  // Load extracted tags when job changes
  useEffect(() => {
    const loadTags = async () => {
      if (!selectedJobId) {
        setExtractedTags([]);
        return;
      }
      
      setTagsLoading(true);
      try {
        const response = await bomApi.loadExtraction(selectedJobId);
        if (response.items) {
          const tags: ExtractedTag[] = response.items.map((item: any) => ({
            reference: item.reference,
            component_type: item.component_type || 'UNKNOWN',
            description: item.description || '',
            pages: item.pages || '',
            verification_status: item.verification_status || 'pending'
          }));
          setExtractedTags(tags);
        }
      } catch (e) {
        setExtractedTags([]);
      } finally {
        setTagsLoading(false);
      }
    };
    loadTags();
  }, [selectedJobId]);

  const handleFolderChange = (folderId: string) => {
    const folder = folders.find(f => f.id === folderId);
    setSelectedFolderId(folderId);
    setSelectedFolderName(folder?.customer_name || '');
    setSelectedJobId('');
    setSelectedPdfName('');
    setExtractedTags([]);
    setBomItems([]);
    setCrossRefs([]);
    setResults(null);
    setActiveStep(0);
  };

  const handleJobChange = (job: any) => {
    if (job) {
      setSelectedJobId(job.job_id || job.id);
      setSelectedPdfName(job.cleanPdfName || cleanPdfFilename(job.pdf_filename));
    } else {
      setSelectedJobId('');
      setSelectedPdfName('');
      setExtractedTags([]);
    }
  };

  const handleRunReconciliation = async () => {
    if (!selectedJobId) {
      showToast('error', 'Please select a PDF extraction');
      return;
    }
    
    setReconciliationLoading(true);
    try {
      console.log('[Reconciliation] Starting re-run for job:', selectedJobId);
      
      const bomUploads = await bomApi.listBOMUploads(undefined, selectedFolderId);
      const bomUploadId = bomUploads.success && bomUploads.uploads.length > 0 
        ? bomUploads.uploads[0].id 
        : undefined;
      
      console.log('[Reconciliation] BOM Upload ID:', bomUploadId);
      
      const response = await bomApi.runReconciliation(selectedJobId, bomUploadId);
      console.log('[Reconciliation] Run response:', response);
      
      if (response.success) {
        const resultsData = await bomApi.getReconciliationResults(selectedJobId);
        console.log('[Reconciliation] Results:', resultsData);
        
        if (resultsData.success) {
          setResults(resultsData);
          showToast('success', `Reconciliation complete: ${response.stats?.auto_accepted || 0} auto-accepted`);
        } else {
          showToast('error', `Failed to load results: ${(resultsData as any).error || 'Unknown error'}`);
        }
      } else {
        showToast('error', `Reconciliation failed: ${response.error || 'Unknown error'}`);
      }
    } catch (e: any) {
      console.error('[Reconciliation] Error:', e);
      showToast('error', e.message || 'Reconciliation failed');
    } finally {
      setReconciliationLoading(false);
    }
  };

  // Export function that accepts data directly (for history items)
  // Uses same format as handleInternalExport for consistency
  const doExportExcel = (data: any, pdfName: string) => {
    if (!data) return;
    
    // Export ALL items from ALL tabs (matches + untagged)
    const allRows: any[] = [];
    
    // Add all matches (from all confidence levels)
    data.matches?.forEach((m: any) => {
      const pagesStr = m.pages ? (typeof m.pages === 'string' ? m.pages : Array.isArray(m.pages) ? m.pages.join(', ') : '') : '';
      allRows.push({
        'Tag No': m.extracted_tag || '',
        'Tag Origin': m.tag_origin || 'Valid',
        'Type/Category': m.component_type || '',
        'Pages': pagesStr,
        'Customer Part Number': m.customer_part_number || '',
        'Supplier Part Number': m.supplier_part_number || m.customer_supplier_part || '',
        'ERP Number': m.erp_pp_number || '',
        'Vendor Item No': m.erp_vendor_item_no || '',
        'Description': m.erp_description || m.bom_description || m.extracted_description || '',
        'Qty': Number(m.quantity || 1),
        'Category': m.erp_item_category || '',
        'Manufacturer': m.erp_manufacturer_name || '',
        'Vendor Code': m.erp_manufacturer_code || '',
        'Confidence %': Math.round((m.confidence || 0) * 100),
        'Status': m.status || '',
        'User Confirmed': m.user_confirmed ? 'Yes' : 'No',
      });
    });
    
    // Add untagged items
    if (data.untagged_items) {
      data.untagged_items.forEach((item: any) => {
        allRows.push({
          'Tag No': '',
          'Tag Origin': 'Untagged',
          'Type/Category': item.component_type || '',
          'Pages': '',
          'Customer Part Number': item.customer_part_number || '',
          'Supplier Part Number': item.supplier_part_number || '',
          'ERP Number': item.erp_pp_number || '',
          'Vendor Item No': item.erp_vendor_item_no || '',
          'Description': item.erp_description || item.description || '',
          'Qty': Number(item.quantity || 1),
          'Category': item.erp_item_category || '',
          'Manufacturer': item.erp_manufacturer_name || '',
          'Vendor Code': item.erp_manufacturer_code || '',
          'Confidence %': '',
          'Status': 'untagged',
          'User Confirmed': 'No',
        });
      });
    }
    
    if (allRows.length === 0) {
      showToast('warning', 'No data to export');
      return;
    }
    
    // Create CSV
    const headers = Object.keys(allRows[0]);
    const csv = [
      headers.join(','),
      ...allRows.map((row: any) => 
        headers.map(h => `"${String(row[h] || '').replace(/"/g, '""')}"`).join(',')
      )
    ].join('\n');
    
    // Download
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reconciliation_${pdfName || 'export'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    
    showToast('success', `Exported ${allRows.length} items to CSV`);
  };

  // ERP Export function that accepts data directly (for history items)
  // Only includes explicitly user-confirmed items (green lines, status === 'confirmed')
  const doExportErp = (data: any, pdfName: string) => {
    if (!data) return;
    
    // Collect only explicitly user-confirmed matches (green lines)
    const confirmedMatches = (data.matches || []).filter((m: any) => m.status === 'confirmed');
    const confirmedUntagged = (data.untagged_items || []).filter((item: any) => item.status === 'confirmed');
    const allConfirmed = [...confirmedMatches, ...confirmedUntagged];
    
    if (allConfirmed.length === 0) {
      showToast('warning', 'No confirmed items to export for ERP.');
      return;
    }
    
    // Group by ERP Number: each part number appears once, qty summed, tags concatenated
    const erpGroups: Record<string, {
      erpNumber: string;
      vendorItemNo: string;
      description: string;
      totalQty: number;
      tags: string[];
    }> = {};
    
    allConfirmed.forEach((m: any) => {
      const erpNumber = m.erp_pp_number || '';
      if (!erpNumber || !String(erpNumber).trim()) return;
      
      if (!erpGroups[erpNumber]) {
        erpGroups[erpNumber] = {
          erpNumber,
          vendorItemNo: m.erp_vendor_item_no || '',
          description: m.erp_description || m.bom_description || m.extracted_description || '',
          totalQty: 0,
          tags: [],
        };
      }
      
      erpGroups[erpNumber].totalQty += Number(m.quantity || 1);
      const tag = m.extracted_tag || '';
      if (tag && !erpGroups[erpNumber].tags.includes(tag)) {
        erpGroups[erpNumber].tags.push(tag);
      }
    });
    
    // Round up qty to nearest 0.1
    const roundUpToTenth = (n: number) => Math.ceil(n * 10) / 10;
    
    const erpRows = Object.values(erpGroups).map(group => ({
      'Type': 'Item',
      'No': group.erpNumber,
      'External Ref. No.': group.vendorItemNo,
      'Description': group.description,
      'Qty': roundUpToTenth(group.totalQty),
      'Component Reference': group.tags.sort().join(', '),
    }));
    
    if (erpRows.length === 0) {
      showToast('warning', 'No items with ERP numbers found among confirmed items.');
      return;
    }
    
    const headers = Object.keys(erpRows[0]);
    const csv = [
      headers.join(','),
      ...erpRows.map((row: any) => headers.map(h => `"${String(row[h] || '').replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `erp_export_${pdfName || 'export'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    
    showToast('success', `ERP Export: ${erpRows.length} unique parts (${allConfirmed.length} confirmed tags)`);
  };

  const handleExportExcel = () => {
    doExportExcel(results, selectedPdfName || 'export');
  };

  const handleExportErp = () => {
    doExportErp(results, selectedPdfName || 'export');
  };

  const handleViewHistoryItem = async (item: any) => {
    // If full data not loaded yet, load it now
    if (!item.reconciliation._fullDataLoaded) {
      setLoadingViewJobId(item.job_id);
      setReconciliationLoading(true);
      try {
        const fullResults = await bomApi.getReconciliationResults(item.job_id);
        if (fullResults.success) {
          setSelectedHistoryItem({
            ...item,
            reconciliation: fullResults
          });
          // Update history list with full data
          setReconciliationHistory(prev => prev.map(h => 
            h.job_id === item.job_id 
              ? { ...h, reconciliation: fullResults }
              : h
          ));
        } else {
          showToast('error', 'Failed to load reconciliation details');
        }
      } catch (e: any) {
        showToast('error', `Failed to load: ${e.message || 'Unknown error'}`);
      } finally {
        setReconciliationLoading(false);
        setLoadingViewJobId(null);
      }
    } else {
      setSelectedHistoryItem(item);
    }
  };

  const handleDeleteReconciliation = async (item: any) => {
    if (!window.confirm(`Are you sure you want to delete the reconciliation for "${item.cleanPdfName}"? This cannot be undone.`)) {
      return;
    }
    
    try {
      const response = await bomApi.deleteReconciliation(item.job_id);
      if (response.success) {
        showToast('success', `Deleted reconciliation (${response.deleted_count} matches)`);
        // Remove from history list
        setReconciliationHistory(prev => prev.filter(h => h.job_id !== item.job_id));
      } else {
        showToast('error', response.error || 'Failed to delete reconciliation');
      }
    } catch (e: any) {
      showToast('error', e.message || 'Failed to delete');
    }
  };

  // Step definitions
  const steps = [
    { label: 'Tag Extraction', icon: <TagIcon />, description: 'Review extracted tags' },
    { label: 'BOM Upload', icon: <BomIcon />, description: 'Upload customer BOM' },
    { label: 'Master List', icon: <LinkIcon />, description: 'Cross-reference (optional)' },
    { label: 'Results', icon: <ReconcileIcon />, description: 'Run reconciliation' }
  ];

  // Show loading while determining initial view
  if (viewMode === null) {
    return (
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, height: '100%' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>
          🔗 Part Reconciliation
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant={viewMode === 'wizard' ? 'contained' : 'outlined'}
            size="small"
            color="primary"
            onClick={() => {
              // Check if user is in the middle of a reconciliation (has data loaded)
              const hasUnsavedWork = viewMode === 'wizard' && (
                selectedFolderId || 
                selectedJobId || 
                extractedTags.length > 0 || 
                bomItems.length > 0 || 
                results
              );
              
              if (hasUnsavedWork) {
                if (!window.confirm('Do you want to leave the current reconciliation? Changes will be lost.')) {
                  return; // User cancelled
                }
              }
              
              // Reset wizard state for a fresh start
              // Note: localMatches/localUntagged are in child component and reset when results=null
              setViewMode('wizard');
              setSelectedHistoryItem(null);
              setActiveStep(0);
              setSelectedFolderId('');
              setSelectedFolderName('');
              setSelectedJobId('');
              setSelectedPdfName('');
              setResults(null);
              setBomItems([]);
              setCrossRefs([]);
              setExtractedTags([]);
            }}
          >
            New Reconciliation
          </Button>
          <Button
            variant={viewMode === 'history' ? 'contained' : 'outlined'}
            size="small"
            startIcon={<HistoryIcon />}
            onClick={() => {
              setViewMode('history');
              setSelectedHistoryItem(null);
            }}
          >
            History {reconciliationHistory.length > 0 && `(${reconciliationHistory.length})`}
          </Button>
        </Box>
      </Box>

      {/* History View */}
      {viewMode === 'history' && !selectedHistoryItem && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Completed Reconciliations</Typography>
          {reconciliationHistory.length === 0 ? (
            <Alert severity="info">No completed reconciliations found.</Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>PDF</TableCell>
                    <TableCell>Tags</TableCell>
                    <TableCell>Auto-Accepted</TableCell>
                    <TableCell>Needs Review</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reconciliationHistory.map((item) => (
                    <TableRow key={item.job_id}>
                      <TableCell>{item.cleanPdfName}</TableCell>
                      <TableCell>{item.reconciliation.stats.total_matches}</TableCell>
                      <TableCell>
                        <Chip size="small" label={item.reconciliation.stats.auto_accepted} color="success" />
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={item.reconciliation.stats.needs_review} color="warning" />
                      </TableCell>
                      <TableCell>{new Date(item.created_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Button 
                            size="small" 
                            startIcon={loadingViewJobId === item.job_id ? <CircularProgress size={16} /> : <ViewIcon />}
                            onClick={() => handleViewHistoryItem(item)}
                            disabled={loadingViewJobId === item.job_id || reconciliationLoading}
                          >
                            {loadingViewJobId === item.job_id ? 'Loading...' : 'View'}
                          </Button>
                          <Button size="small" startIcon={<DownloadIcon />} onClick={async () => {
                            // Load full data if not already loaded
                            if (!item.reconciliation._fullDataLoaded) {
                              try {
                                const fullResults = await bomApi.getReconciliationResults(item.job_id);
                                if (fullResults.success) {
                                  doExportExcel(fullResults, item.cleanPdfName);
                                  setReconciliationHistory(prev => prev.map(h => 
                                    h.job_id === item.job_id 
                                      ? { ...h, reconciliation: fullResults }
                                      : h
                                  ));
                                } else {
                                  showToast('error', 'Failed to load data for export');
                                }
                              } catch (e: any) {
                                showToast('error', `Export failed: ${e.message || 'Unknown error'}`);
                              }
                            } else {
                              doExportExcel(item.reconciliation, item.cleanPdfName);
                            }
                          }}>
                            Export
                          </Button>
                          <Button size="small" variant="contained" color="primary" startIcon={<DownloadIcon />} onClick={async () => {
                            if (!item.reconciliation._fullDataLoaded) {
                              try {
                                const fullResults = await bomApi.getReconciliationResults(item.job_id);
                                if (fullResults.success) {
                                  doExportErp(fullResults, item.cleanPdfName);
                                  setReconciliationHistory(prev => prev.map(h => 
                                    h.job_id === item.job_id 
                                      ? { ...h, reconciliation: fullResults }
                                      : h
                                  ));
                                } else {
                                  showToast('error', 'Failed to load data for ERP export');
                                }
                              } catch (e: any) {
                                showToast('error', `ERP Export failed: ${e.message || 'Unknown error'}`);
                              }
                            } else {
                              doExportErp(item.reconciliation, item.cleanPdfName);
                            }
                          }}>
                            ERP
                          </Button>
                          <Tooltip title="Delete this reconciliation">
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => handleDeleteReconciliation(item)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* History Detail View */}
      {viewMode === 'history' && selectedHistoryItem && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Button 
              startIcon={<BackIcon />} 
              onClick={() => setSelectedHistoryItem(null)}
            >
              Back to History
            </Button>
            <Typography variant="h6">
              {selectedHistoryItem.cleanPdfName}
            </Typography>
            {selectedHistoryItem.reconciliation?.stats ? (
              <Chip label={`${selectedHistoryItem.reconciliation.stats.total_matches} tags`} />
            ) : (
              <Chip 
                icon={<CircularProgress size={16} />} 
                label="Loading..." 
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
          
          {reconciliationLoading && !selectedHistoryItem.reconciliation._fullDataLoaded ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>Loading reconciliation details...</Typography>
            </Paper>
          ) : selectedHistoryItem.reconciliation ? (
            <Paper sx={{ p: 2 }}>
              <StepReconciliationResults
                results={selectedHistoryItem.reconciliation}
                loading={reconciliationLoading}
              onRunReconciliation={async () => {
                // Re-run reconciliation for this history item
                const jobId = selectedHistoryItem.job_id;
                const folderId = selectedHistoryItem.folder_id;
                if (!jobId) {
                  showToast('error', 'Job ID not found');
                  return;
                }
                setReconciliationLoading(true);
                try {
                  const bomUploads = await bomApi.listBOMUploads(undefined, folderId);
                  const bomUploadId = bomUploads.success && bomUploads.uploads.length > 0 
                    ? bomUploads.uploads[0].id 
                    : undefined;
                  
                  const response = await bomApi.runReconciliation(jobId, bomUploadId);
                  if (response.success) {
                    const resultsData = await bomApi.getReconciliationResults(jobId);
                    if (resultsData.success) {
                      // Update the selected history item with new results
                      setSelectedHistoryItem({
                        ...selectedHistoryItem,
                        reconciliation: resultsData
                      });
                      showToast('success', `Re-run complete: ${response.stats.auto_accepted} auto-accepted`);
                    }
                  }
                } catch (e: any) {
                  showToast('error', e.message);
                } finally {
                  setReconciliationLoading(false);
                }
              }}
              onExportExcel={() => {
                setResults(selectedHistoryItem.reconciliation);
                setSelectedPdfName(selectedHistoryItem.cleanPdfName);
                handleExportExcel();
              }}
              onExportErp={() => {
                setResults(selectedHistoryItem.reconciliation);
                setSelectedPdfName(selectedHistoryItem.cleanPdfName);
                handleExportErp();
              }}
              onRestart={() => {
                // Restart reconciliation - go back to wizard with this PDF's data pre-loaded
                const jobId = selectedHistoryItem.job_id;
                const folderId = selectedHistoryItem.folder_id;
                const folderName = selectedHistoryItem.folder_name || '';
                const pdfName = selectedHistoryItem.cleanPdfName;
                
                // Pre-populate wizard with this reconciliation's data
                setSelectedFolderId(folderId);
                setSelectedFolderName(folderName);
                setSelectedJobId(jobId);
                setSelectedPdfName(pdfName);
                
                // Load tags from the extraction
                if (selectedHistoryItem.reconciliation?.matches) {
                  const tags = selectedHistoryItem.reconciliation.matches.map((m: any) => ({
                    reference: m.extracted_tag,
                    component_type: m.component_type,
                    pages: m.pages,
                    tag_origin: m.tag_origin || 'Valid'
                  }));
                  setExtractedTags(tags);
                }
                
                // Clear results so wizard shows fresh
                setResults(null);
                setBomItems([]);
                setCrossRefs([]);
                setActiveStep(0);
                
                // Switch to wizard mode
                setSelectedHistoryItem(null);
                setViewMode('wizard');
                
                showToast('info', 'Reconciliation reset - you can now re-upload BOM and run again');
              }}
              showToast={showToast}
            />
          </Paper>
          ) : null}
        </Box>
      )}

      {/* Wizard View */}
      {viewMode === 'wizard' && (
        <>
          {/* Folder & PDF Selection */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <FormControl sx={{ minWidth: 200 }} size="small">
                <InputLabel>Customer Folder</InputLabel>
                <Select
                  value={selectedFolderId}
                  label="Customer Folder"
                  onChange={(e) => handleFolderChange(e.target.value)}
                >
                  <MenuItem value="">-- Select Folder --</MenuItem>
                  {folders.map(folder => (
                    <MenuItem key={folder.id} value={folder.id}>{folder.customer_name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Autocomplete
                sx={{ minWidth: 300, flexGrow: 1 }}
                size="small"
                options={jobs}
                getOptionLabel={(option: any) => 
                  option.cleanPdfName 
                    ? `${option.cleanPdfName} (${option.total_candidates || 0} tags)`
                    : ''
                }
                value={jobs.find((j: any) => (j.job_id || j.id) === selectedJobId) || null}
                onChange={(_, newValue) => handleJobChange(newValue)}
                disabled={!selectedFolderId}
                renderInput={(params) => (
                  <TextField {...params} label="PDF Extraction" placeholder="Search PDFs..." />
                )}
              />
            </Box>
          </Paper>

          {/* Wizard Content */}
          {selectedFolderId && selectedJobId && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              {/* Collapsible Stepper */}
              <Paper sx={{ 
                p: sidebarCollapsed ? 1 : 2, 
                minWidth: sidebarCollapsed ? 50 : 220,
                transition: 'all 0.3s',
                position: 'relative'
              }}>
                <IconButton 
                  size="small" 
                  onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                  sx={{ position: 'absolute', right: 4, top: 4 }}
                >
                  {sidebarCollapsed ? <ExpandIcon /> : <CollapseIcon />}
                </IconButton>
                
                {!sidebarCollapsed && (
                  <Stepper activeStep={activeStep} orientation="vertical" sx={{ mt: 3 }}>
                    {steps.map((step, index) => (
                      <Step key={step.label}>
                        <StepLabel
                          onClick={() => setActiveStep(index)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {step.icon}
                            <Box>
                              <Typography variant="body2">{step.label}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {step.description}
                              </Typography>
                            </Box>
                          </Box>
                        </StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                )}
                
                {sidebarCollapsed && (
                  <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
                    {steps.map((step, index) => (
                      <Tooltip key={index} title={step.label} placement="right">
                        <IconButton 
                          onClick={() => setActiveStep(index)}
                          color={activeStep === index ? 'primary' : 'default'}
                          size="small"
                        >
                          {step.icon}
                        </IconButton>
                      </Tooltip>
                    ))}
                  </Box>
                )}
              </Paper>

              {/* Step Content */}
              <Paper sx={{ p: 3, flex: 1, overflow: 'auto' }}>
                {activeStep === 0 && (
                  <Step1TagExtraction 
                    tags={extractedTags}
                    loading={tagsLoading}
                    pdfName={selectedPdfName}
                  />
                )}
                
                {activeStep === 1 && (
                  <Step2BOMUpload
                    folderId={selectedFolderId}
                    folderName={selectedFolderName}
                    bomItems={bomItems}
                    setBomItems={setBomItems}
                    onUploadComplete={() => {}}
                    showToast={showToast}
                  />
                )}
                
                {activeStep === 2 && (
                  <Step3CrossReference
                    folderId={selectedFolderId}
                    folderName={selectedFolderName}
                    crossRefs={crossRefs}
                    setCrossRefs={setCrossRefs}
                    showToast={showToast}
                  />
                )}
                
                {activeStep === 3 && (
                  <StepReconciliationResults
                    results={results}
                    loading={reconciliationLoading}
                    onRunReconciliation={handleRunReconciliation}
                    onExportExcel={handleExportExcel}
                    onExportErp={handleExportErp}
                    showToast={showToast}
                  />
                )}

                {/* Navigation */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Button
                    disabled={activeStep === 0}
                    onClick={() => setActiveStep(activeStep - 1)}
                    startIcon={<BackIcon />}
                  >
                    Back
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => {
                      if (activeStep < steps.length - 1) {
                        setActiveStep(activeStep + 1);
                      }
                    }}
                    endIcon={activeStep < steps.length - 1 ? <NextIcon /> : undefined}
                    disabled={activeStep === steps.length - 1}
                  >
                    {activeStep === steps.length - 1 ? 'Complete' : 'Next'}
                  </Button>
                </Box>
              </Paper>
            </Box>
          )}

          {/* Empty state */}
          {(!selectedFolderId || !selectedJobId) && (
            <Alert severity="info">
              Select a customer folder and PDF extraction to begin the reconciliation wizard.
            </Alert>
          )}
        </>
      )}
    </Box>
  );
};

export default ReconciliationView;
